import { loadWeeklyCalendarCounts } from './components/mobileCalendarCounts';
import MobileJobActions from './components/MobileJobActions';
import useMobileSchedule from '../../hooks/useMobileSchedule';
import MobileScheduleMoveDialog from './components/MobileScheduleMoveDialog';
// src/pages/Schedule/AssignmentSchedule.jsx
import { memo, useCallback, useMemo, useState, useEffect, useRef } from "react";
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
    FiHome, FiLayers, FiCalendar, FiChevronLeft, FiChevronRight,
    FiX, FiMove, FiHash, FiUser, FiTool, FiDollarSign,
    FiPackage, FiChevronsLeft, FiChevronsRight, FiInfo,
    FiSearch, FiTruck,
    FiEdit2, FiTrash2, FiPlus, FiMessageSquare, FiMapPin,
    FiAlertCircle
} from "react-icons/fi";
import { TbTruckDelivery } from "react-icons/tb";
import { GiWoodBeam } from "react-icons/gi";
import { BrickWall, Grid2X2, MapIcon, UserPen, PartyPopper, PanelTop, Square, SquareCheckBig } from "lucide-react";
import {
    startOfWeek,
    endOfWeek,
    addWeeks,
    format,
    isSameWeek,
    isBefore,
    startOfDay,
    isToday,
    addDays,
    differenceInWeeks
} from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerStyles.css';
import { useHolidays } from '../../hooks/useHolidays';
import TextTooltip from '../common/TextTooltip';

import {
    DndContext,
    DragOverlay,
    useSensor,
    useSensors,
    PointerSensor,
    pointerWithin,
    closestCenter,
    MeasuringStrategy
} from '@dnd-kit/core';
import { useDroppable, defaultDropAnimationSideEffects } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { useVirtualizer } from '@tanstack/react-virtual';

import ConfirmationPopup from "../common/ConfirmationPopup";
import { DayColumnSkeleton, UnscheduledSectionSkeleton } from "../common/SkeletonLoader";

import {
    getScheduleAssignments,
    getUnscheduledByQuote,
    getQuotePlaygroundAssignments,
    updateFinishDate,
    getAvailableUsers,
    batchUpdateSortOrder,
    resetSortOrder,
    toggleTimberIsChecked,
    setQuotePlayground,
    updateQuoteQuickNotes,
} from "../../lib/supabase/schedule";
import {
    subscribeToJobUpdates,
    subscribeToJobQuotesUpdates,
    subscribeToJobDesignersUpdates,
    subscribeToJobEstimatorsUpdates,
    unsubscribeFromJobUpdates
} from "../../lib/supabase/realtime";
import BackButton from "../common/BackButton";
// import JobQuoteStatus from "./components/JobQuoteStatus";
import { getJobStatusSummary } from "../../lib/common/jobStatusText";
import AssignmentContextMenu from "./components/AssignmentContextMenu";
import { toggleQuoteHold, toggleLayoutOnly, toggleWaitingJob, toggleSentProduction } from "../../lib/supabase/assignments";
import Toast from "../common/Toast";
import OnHoldPanel from "./components/OnHoldPanel";
import ScheduleActivityLog from "./ScheduleActivityLog";
import { createJobActionLog } from "../../lib/supabase/activityLog";
// import WaitingJobsPanel from "./components/WaitingJobsPanel";
import EditAssignment from "../assignments/EditAssignment";
import { getInvoiceSourceIcon, getInvoiceStatusText } from "../../lib/common/invoiceDisplay";
import MonthlyInvoiceView from "./MonthlyInvoiceView.js";
import OutOfWeekJobSearch from "./components/OutOfWeekJobSearch";

const quoteScheduleCollision = (args) => {
    const px = args.pointerCoordinates?.x ?? 0;
    const py = args.pointerCoordinates?.y ?? 0;

    const zoneIds = ['unscheduled-zone', 'playground-zone', 'waiting-zone'];
    for (const id of zoneIds) {
        const container = args.droppableContainers.find(c => c.id === id && !c.disabled);
        if (!container) continue;
        const rect = container.rect.current;
        if (!rect) continue;
        if (px >= rect.left && px <= rect.right && py >= rect.top && py <= rect.bottom) {
            return [{ id }];
        }
    }

    const results = pointerWithin(args);
    return results.length ? results : closestCenter(args);
};

const formatNoteDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) +
        ' at ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const formatSmDate = (dateValue) => {
    if (!dateValue) return 'No date';

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'Invalid date';

    return date.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

const DroppableUnscheduled = ({ children }) => {
    const { isOver, setNodeRef } = useDroppable({ id: 'unscheduled-zone' });
    return (
        <div
            ref={setNodeRef}
            className={`bg-white border-2 border-dashed ${isOver ? 'border-orange-300 bg-orange-50' : 'border-gray-300'} rounded-xl p-6 hover:border-orange-300 transition-colors`}
        >
            {children}
        </div>
    );
};

const DroppablePlayground = ({ children }) => {
    const { isOver, setNodeRef } = useDroppable({ id: 'playground-zone' });
    return (
        <div
            ref={setNodeRef}
            className={`bg-white border-2 border-dashed ${isOver ? 'border-purple-400 bg-purple-50' : 'border-gray-300'} rounded-xl p-6 hover:border-purple-300 transition-colors h-full`}
        >
            {children}
        </div>
    );
};

const DroppableDatePickerZone = ({ children }) => {
    const { isOver, setNodeRef } = useDroppable({ id: 'date-picker-drop-zone' });
    return (
        <div
            ref={setNodeRef}
            className={`flex-1 text-center min-w-0 transition-all ${isOver ? 'ring-4 ring-blue-400 ring-opacity-50 bg-blue-50 rounded-lg' : ''}`}
        >
            {children}
        </div>
    );
};

const QuickNotesButton = ({ assignment, userProfile, isOpen, onToggle, bubblePos, setScheduleData, setUnscheduledAssignments, setPlaygroundAssignments }) => {
    const quoteId = assignment?.quote_info?.id;
    const notes = assignment?.quote_info?.quick_notes || [];
    const hasNotes = notes.length > 0;

    const [newNoteText, setNewNoteText] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [saving, setSaving] = useState(false);
    const btnRef = useRef(null);
    const bubbleRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e) => {
            if (bubbleRef.current && !bubbleRef.current.contains(e.target) &&
                btnRef.current && !btnRef.current.contains(e.target)) {
                onToggle(false, null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const openBubble = (e) => {
        e.stopPropagation();
        if (isOpen) { onToggle(false, null); return; }
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        const bubbleWidth = 320;
        const bubbleHeight = 400;
        const flipLeft = rect.left + bubbleWidth > window.innerWidth - 12;
        const spaceBelow = window.innerHeight - rect.bottom;
        const showAbove = spaceBelow < bubbleHeight && rect.top > bubbleHeight;
        const pos = { x: flipLeft ? rect.right : rect.left, y: showAbove ? rect.top - 8 : rect.bottom + 8, showAbove, flipLeft };
        onToggle(true, pos);
    };

    const saveNotes = async (updated) => {
        if (!quoteId) return;
        setSaving(true);
        try {
            await updateQuoteQuickNotes(quoteId, updated);
            const patchItem = (item) => {
                if (item.quote_info?.id !== quoteId) return item;
                return { ...item, quote_info: { ...item.quote_info, quick_notes: updated } };
            };
            setScheduleData(prev => {
                const next = {};
                for (const dk in prev) next[dk] = prev[dk].map(patchItem);
                return next;
            });
            setUnscheduledAssignments(prev => prev.map(patchItem));
            setPlaygroundAssignments(prev => prev.map(patchItem));
        } catch (err) {
            console.error('Failed to save quick notes:', err);
            Toast.error('Failed to save note');
        } finally {
            setSaving(false);
        }
    };

    const handleAddNote = async () => {
        const text = newNoteText.trim();
        if (!text) return;
        const newNote = {
            id: crypto.randomUUID(),
            note: text,
            type: assignment?.quote_info?.quote_type || null,
            added_by_id: userProfile?.id || null,
            added_by_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
            added_at: new Date().toISOString(),
            edited_at: null,
        };
        setNewNoteText('');
        await saveNotes([...notes, newNote]);
    };

    const handleEditSave = async (noteId) => {
        const text = editingText.trim();
        if (!text) return;
        const updated = notes.map(n => n.id === noteId ? { ...n, note: text, edited_at: new Date().toISOString() } : n);
        setEditingId(null);
        await saveNotes(updated);
    };

    const handleDelete = async (noteId) => {
        await saveNotes(notes.filter(n => n.id !== noteId));
    };

    const bubbleJsx = isOpen ? (
        <div
            ref={bubbleRef}
            className="fixed z-[9990] w-80 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden"
            style={{
                [bubblePos.flipLeft ? 'right' : 'left']: bubblePos.flipLeft ? window.innerWidth - bubblePos.x : bubblePos.x,
                [bubblePos.showAbove ? 'bottom' : 'top']: bubblePos.showAbove ? window.innerHeight - bubblePos.y : bubblePos.y,
            }}
            onClick={e => e.stopPropagation()}
        >
            <div className="px-3 pt-3 pb-2">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Job Info</span>
                    <button type="button" onClick={() => onToggle(false, null)} className="text-gray-400 hover:text-gray-600">
                        <FiX className="w-3.5 h-3.5" />
                    </button>
                </div>
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                        <FiHash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs font-semibold text-gray-800">{assignment.unique_id}</span>
                        {assignment.quote_info?.child_unique_id && (
                            <span className="text-xs font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                {assignment.quote_info.child_unique_id}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <FiUser className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-700 font-medium">{assignment.customer_name}</span>
                    </div>
                    {assignment.customer_address && (
                        <div className="flex items-start gap-2">
                            <FiMapPin className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-gray-600">{assignment.customer_address}</span>
                        </div>
                    )}
                </div>
            </div>
            <div className="border-t border-gray-100" />
            <div className="px-3 pt-2 pb-3 max-h-64 overflow-y-auto">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Quick Notes</span>
                {notes.length === 0 && <p className="text-xs text-gray-400 italic mt-1 mb-2">No notes yet</p>}
                <div className="space-y-2 mt-1">
                    {notes.map(n => (
                        <div key={n.id} className="bg-gray-50 rounded-lg p-2 text-xs">
                            {editingId === n.id ? (
                                <div className="space-y-1">
                                    <textarea
                                        autoFocus
                                        className="w-full text-xs border border-blue-300 rounded p-1 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                                        rows={2}
                                        value={editingText}
                                        onChange={e => setEditingText(e.target.value)}
                                    />
                                    <div className="flex gap-1">
                                        <button type="button" onClick={() => handleEditSave(n.id)} disabled={saving}
                                            className="px-2 py-0.5 bg-blue-600 text-white rounded text-[10px] font-medium disabled:opacity-50">Save</button>
                                        <button type="button" onClick={() => setEditingId(null)}
                                            className="px-2 py-0.5 text-gray-500 hover:text-gray-700 text-[10px]">Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-start justify-between gap-1">
                                        <p className="text-gray-800 flex-1 leading-snug">{n.note}</p>
                                        <div className="flex gap-1 flex-shrink-0">
                                            <button type="button" onClick={() => { setEditingId(n.id); setEditingText(n.note); }}
                                                className="text-gray-400 hover:text-blue-600 p-0.5">
                                                <FiEdit2 className="w-3 h-3" />
                                            </button>
                                            <button type="button" onClick={() => handleDelete(n.id)} disabled={saving}
                                                className="text-gray-400 hover:text-red-500 p-0.5 disabled:opacity-50">
                                                <FiTrash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="text-[10px] text-gray-400 mt-1">
                                        {n.added_by_name} · {formatNoteDate(n.edited_at || n.added_at)}
                                        {n.edited_at && <span className="ml-1 italic">(edited)</span>}
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
                <div className="mt-2 space-y-1">
                    <textarea
                        className="w-full text-xs border border-gray-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                        rows={2}
                        placeholder="Add a quick note…"
                        value={newNoteText}
                        onChange={e => setNewNoteText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
                    />
                    <button type="button" onClick={handleAddNote} disabled={!newNoteText.trim() || saving}
                        className="flex items-center gap-1 text-[11px] px-2 py-1 bg-blue-600 text-white rounded-lg disabled:opacity-40 hover:bg-blue-700 font-medium">
                        <FiPlus className="w-3 h-3" /> Add Note
                    </button>
                </div>
            </div>
        </div>
    ) : null;

    return (
        <>
            <button
                ref={btnRef}
                aria-label="Quick notes"
                type="button"
                className={`p-1.5 rounded-lg border transition-all shadow-md ${hasNotes
                    ? 'bg-blue-500 text-white border-blue-600 hover:bg-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}`}
                onClick={openBubble}
            >
                <FiMessageSquare className="w-3.5 h-3.5" />
            </button>
            {createPortal(bubbleJsx, document.body)}
        </>
    );
};

// ─── Module-level pure helpers (stable references, no closures over component state) ───

const formatStatus = (status) => {
    if (!status) return "Pending";
    return status
        .replace(/_/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase());
};

const cardStatusStyle = (assignment, type = "bar") => {
    const getColor = (status) => {
        switch (status) {
            case "waiting_assignment":
            case "not_required":
            case "waiting_design":
            case "not_started":
            case "not_scheduled":
                return { bar: "bg-gray-400", text: "text-gray-600" };
            case "in_progress":
            case "in_cutting":
            case "in_production":
            case "in_transit":
            case "scheduled":
                return { bar: "bg-yellow-600", text: "text-yellow-700" };
            case "completed":
            case "delivered":
                return { bar: "bg-green-600", text: "text-green-700" };
            case "cancelled":
                return { bar: "bg-red-600", text: "text-red-700" };
            // case "not_required":
            //     return { bar: "bg-green-200", text: "text-green-600" };
            default:
                return { bar: "bg-gray-200", text: "text-gray-500" };
        }
    };
    return getColor(assignment?.design_status)[type];
};

// InfoTooltip only depends on `assignment` prop — safe at module level
const InfoTooltip = ({ assignment }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, showAbove: false });
    const [triggerRef, setTriggerRef] = useState(null);

    useEffect(() => {
        if (isHovered && triggerRef) {
            const rect = triggerRef.getBoundingClientRect();
            const tooltipHeight = 280;
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;
            setTooltipPosition({
                x: rect.left,
                y: showAbove ? rect.top - 8 : rect.bottom + 8,
                showAbove
            });
        }
    }, [isHovered, triggerRef]);

    const getStatusDot = (status) => {
        switch (status) {
            case 'completed': return 'bg-green-500';
            case 'delivered': return 'bg-green-500';
            case 'in_progress': return 'bg-yellow-500';
            case 'in_transit': return 'bg-yellow-500';
            case 'in_cutting': return 'bg-yellow-500';
            case 'in_production': return 'bg-yellow-500';
            case 'scheduled': return 'bg-blue-500';
            case 'cutting_ended': return 'bg-blue-500';
            case 'waiting_assignment': return 'bg-red-500';
            case 'waiting_design': return 'bg-red-500';
            case 'not_scheduled': return 'bg-red-500';
            case 'not_started': return 'bg-red-500';
            case 'not_required': return 'bg-red-500';
            case 'cancelled': return 'bg-gray-600';
            default: return 'bg-gray-400';
        }
    };

    const TooltipContent = () => (
        <div
            className="fixed z-[9980] w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-3 pointer-events-none"
            style={{
                left: `${tooltipPosition.x}px`,
                top: tooltipPosition.showAbove ? `${tooltipPosition.y}px` : `${tooltipPosition.y}px`,
                transform: `${tooltipPosition.x > window.innerWidth - 320 ? 'translateX(-100%)' : 'translateX(0)'} ${tooltipPosition.showAbove ? 'translateY(-100%)' : 'translateY(0)'}`
            }}
        >
            <div
                className={`absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45 ${tooltipPosition.showAbove ? 'border-t-0 border-l-0' : 'border-b-0 border-r-0'}`}
                style={{
                    [tooltipPosition.showAbove ? 'bottom' : 'top']: '-6px',
                    left: tooltipPosition.x > window.innerWidth - 320 ? 'calc(100% - 20px)' : '12px'
                }}
            ></div>
            <div className="space-y-2">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">Job Information</h4>
                <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                        <FiCalendar className="w-3.5 h-3.5 text-blue-600" />
                        <span className="font-medium">Expected Delivery</span>
                    </div>
                    <span className="text-gray-700 font-medium">
                        {(assignment.quote_info?.expected_delivery_date ?? assignment.quote_info?.expected_finish_date)
                            ? new Date(assignment.quote_info.expected_delivery_date ?? assignment.quote_info.expected_finish_date).toLocaleDateString('en-AU', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                            })
                            : 'Not set'}
                    </span>
                </div>
                {assignment?.job_type?.toLowerCase()?.includes("estimation") && (
                    <>
                        <div className="flex items-center justify-between text-xs mb-1">
                            <div className="flex items-center gap-2">
                                <FiUser className="w-3 h-3 text-teal-600" />
                                <span className="font-medium">Estimator</span>
                            </div>
                            <span className="text-gray-700 font-medium">
                                {assignment?.job_estimators?.name || 'Not assigned'}
                            </span>
                        </div>
                        <div className="flex items-center justify-between text-xs mb-1">
                            <div className="flex items-center gap-2">
                                <FiDollarSign className="w-3 h-3 text-green-600" />
                                <span className="font-medium">Estimation Status</span>
                                <div className={`w-2 h-2 rounded-full ${getStatusDot(assignment?.quote_info?.estimation_status)}`}></div>
                            </div>
                            <span className="text-gray-700 capitalize">
                                {assignment?.quote_info?.estimation_status?.replace('_', ' ') || 'N/A'}
                            </span>
                        </div>
                    </>
                )}
                {assignment.job_quotes?.filter(q => q.is_required).map((quote) => {
                    const isThisCardsQuote = quote.id === assignment?.quote_info?.id;
                    const designer = assignment?.job_designers?.find(d =>
                        d.child_job_id ? d.child_job_id === quote.id : d.quote_type === quote.quote_type
                    );
                    const quoteLabel = `${quote.quote_type}${quote.child_unique_id ? ` · ${quote.child_unique_id}` : ''}`;
                    if (!isThisCardsQuote && quote?.child_unique_id) {
                        return (
                            <div key={quote.id} className="border-t border-gray-100 pt-2">
                                <div className="font-medium text-xs text-gray-900 mb-1 capitalize flex flex-row justify-between items-center">
                                    <div className="flex items-center gap-1.5">
                                        <span>{quoteLabel}</span>
                                        <span className="text-gray-400 font-normal">
                                            {designer ? `· ${designer.name}` : '· Not assigned'}
                                        </span>
                                    </div>
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusDot(quote.design_status)}`}></div>
                                </div>
                            </div>
                        );
                    }
                    const production = assignment?.job_production?.find(p => p.quote_type === quote.quote_type);
                    const delivery = assignment?.job_deliveries?.find(d => d.quote_type === quote.quote_type);
                    return (
                        <div key={quote.id} className="border-t border-gray-100 pt-2">
                            <div className="font-medium text-xs text-gray-900 mb-2 capitalize">{quoteLabel}</div>
                            <div className="flex items-center justify-between text-xs mb-1">
                                <div className="flex items-center gap-2">
                                    <FiUser className="w-3 h-3 text-indigo-600" />
                                    <span className="font-medium">Designer</span>
                                </div>
                                <span className="text-gray-700 font-medium">{designer?.name || 'Not assigned'}</span>
                            </div>
                            <div className="flex items-center justify-between text-xs mb-1">
                                <div className="flex items-center gap-2">
                                    <FiLayers className="w-3 h-3 text-blue-600" />
                                    <span className="font-medium">Design Status</span>
                                    <div className={`w-2 h-2 rounded-full ${getStatusDot(quote.design_status)}`}></div>
                                </div>
                                <span className="text-gray-700 capitalize">{quote.design_status?.replace('_', ' ') || 'N/A'}</span>
                            </div>
                            <div className="flex items-center justify-between text-xs mb-1">
                                <div className="flex items-center gap-2">
                                    <FiTool className="w-3 h-3 text-purple-600" />
                                    <span className="font-medium">Production</span>
                                    <div className={`w-2 h-2 rounded-full ${getStatusDot(production?.phase || quote.production_status)}`}></div>
                                </div>
                                <span className="text-gray-700 capitalize">
                                    {(production?.phase || quote.production_status || 'not_started')?.replace('_', ' ')}
                                </span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                    <TbTruckDelivery className="w-3.5 h-3.5 text-red-600" />
                                    <span className="font-medium">Delivery</span>
                                    <div className={`w-2 h-2 rounded-full ${getStatusDot(delivery?.delivery_status)}`}></div>
                                </div>
                                <span className="text-gray-700 capitalize">
                                    {delivery?.delivery_status?.replace('_', ' ') || 'Not scheduled'}
                                </span>
                            </div>
                        </div>
                    );
                })}
                <div className="border-t border-gray-100 pt-2 mt-2 flex items-center gap-3 text-[10px] text-gray-500">
                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-500"></div><span>Completed</span></div>
                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-yellow-500"></div><span>In Progress</span></div>
                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500"></div><span>Waiting</span></div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <button
                ref={setTriggerRef}
                aria-label="Job information"
                className="p-1.5 rounded-lg bg-white text-gray-600 hover:bg-gray-100 border border-gray-300 transition-all shadow-md"
                onClick={e => { e.stopPropagation(); if (window.matchMedia('(max-width: 767px)').matches) setIsHovered(value => !value); }}
                onMouseEnter={() => { if (!window.matchMedia('(max-width: 767px)').matches) setIsHovered(true); }}
                onMouseLeave={() => { if (!window.matchMedia('(max-width: 767px)').matches) setIsHovered(false); }}
            >
                <FiInfo className="w-3.5 h-3.5" />
            </button>
            {isHovered && createPortal(<TooltipContent />, document.body)}
        </>
    );
};

// SortableCard: the full draggable assignment card.
// Defined at MODULE LEVEL so React never treats it as a new component type across
// parent re-renders — preventing the unmount/remount storm that killed drag performance.
// memo() means individual cards only re-render when their own data or stable callbacks change.
const DEFAULT_NOTES_POS = { x: 0, y: 0, showAbove: false, flipLeft: false };

const SortableCard = memo(function SortableCard({
    assignment,
    playgroundMode,
    isDisabled,
    quoteType,
    userRole,
    isMobile,
    companySlug,
    navigate,
    notesOpen,
    notesBubblePos,
    onOpen,
    onEdit,
    onDuplicateJob,
    onToggleQuoteHold,
    onToggleLayoutOnly,
    onToggleWaiting,
    onToggleSentProduction,
    onMobileMove,
    onToggleTimberChecked,
    setOpenNotesBubble,
    setScheduleData,
    setUnscheduledAssignments,
    setPlaygroundAssignments,
    userProfile,
}) {
    const baseId = assignment?.quote_info?.id || assignment?.id;
    const isAdmin = userRole === 'admin' || userRole === 'super_admin';
    const location = useLocation();
    const { company } = useAuth();

    const sortableId = playgroundMode ? `playground-${baseId}` : baseId;
    // Stable data object — a new object reference on every render forces dnd-kit to
    // re-register the item, causing extra work for 300 cards. useMemo prevents that.
    const sortableData = useMemo(
        () => ({ assignment, ...(playgroundMode && { fromPlayground: true }) }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [baseId, playgroundMode]
    );

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging,
    } = useSortable({
        id: sortableId,
        data: sortableData,
        disabled: isDisabled || isMobile,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        // Disable CSS transitions entirely for large lists — 300 cards each running
        // a 200ms transition on every drag-over event is the primary lag source.
        transition: null,
        // Hide the original item while dragging — DragOverlay shows the floating clone.
        opacity: isDragging ? 0 : 1,
        // CSS containment: tell the browser this card's layout/paint is self-contained.
        // Prevents style recalculations from bubbling up the tree on every transform.
        contain: 'layout style paint',
        // Promote to its own GPU layer only while actively dragging this card.
        willChange: isDragging ? 'transform' : undefined,
    };

    const assignedDesigner = assignment?.designers?.[0] || null;

    const INVOICE_STYLES = {
        null: "text-red-800 bg-red-200",
        not_invoiced: "text-red-800 bg-red-200",
        undefined: "text-red-800 bg-red-200",
        deleted: "text-red-800 bg-red-200",

        draft: "text-blue-800 bg-blue-200",
        submitted: "text-blue-800 bg-blue-200",
        needs_approval: "text-blue-800 bg-blue-200",

        deposit: "text-yellow-800 bg-yellow-200",

        invoiced: "text-green-800 bg-green-200",
        authorised: "text-green-800 bg-green-200",
        cod: "text-green-800 bg-green-200",
        paid: "text-green-800 bg-green-200",

        voided: "text-zinc-800 bg-zinc-200",
        cancelled: "text-zinc-800 bg-zinc-200",
    };


    const quoteTypeForSm = assignment?.quote_info?.quote_type || quoteType;

    const SITE_MEASURE_JOB_FIELD = {
        roof: 'site_measure_truss',
        wall: 'site_measure_wall',
        floor: 'site_measure_floor',
    };

    const needsSiteMeasure = Boolean(
        assignment?.[SITE_MEASURE_JOB_FIELD[quoteTypeForSm]] && assignment?.quote_info?.site_measure_date !== null
    );


    const siteMeasureConfirmed = Boolean(
        assignment?.quote_info?.site_measure_confirmed
    );

    const siteMeasureDate = assignment?.quote_info?.site_measure_date;

    const siteMeasureTooltip = `Site Measure: ${siteMeasureConfirmed ? 'Confirmed' : 'Not Confirmed'
        }${siteMeasureDate ? ` • ${formatSmDate(siteMeasureDate)}` : ' • No date'}`;


    const showWallQuoteIndicator =
        quoteType === 'roof' || quoteType === 'floor';

    const hasWallQuote = Boolean(
        assignment?.job_quotes?.some((quote) =>
            quote?.quote_type === 'wall' &&
            quote?.is_required !== false
        )
    );

    return (
        <AssignmentContextMenu
            assignment={assignment}
            onOpenAssignment={onOpen}
            onEditAssignment={onEdit}
            onDuplicateJob={onDuplicateJob}
            onToggleQuoteHold={onToggleQuoteHold}
            onToggleLayoutOnly={onToggleLayoutOnly}
            onToggleWaiting={onToggleWaiting}
            onToggleSentProduction={onToggleSentProduction}
            page="department"
            userRole={userRole}
        >
            <div
                ref={setNodeRef}
                style={style}
                {...(isMobile ? {} : attributes)}
                {...(notesOpen ? {} : listeners)}
                className={` relative border-2 rounded-lg p-3 pb-2 shadow-sm hover:shadow-md transition-shadow ${isAdmin ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${assignment.urgent_design ? "bg-red-50 border-red-200" : "bg-white border-gray-200"}`}
                onClick={() => navigate(`/${companySlug}/assignment/${assignment.id}`, {
                    state: { from: location.pathname }
                })}
            >
                {/* Status Progress Bar */}
                {(() => {
                    const qt = assignment.quote_info?.quote_type;
                    const qId = assignment.quote_info?.quoteId || assignment.quote_info?.id;
                    const designStatus = assignment.quote_info?.design_status || 'waiting_assignment';
                    const designColor =
                        designStatus === 'completed' ? 'bg-green-500' :
                            designStatus === 'in_progress' ? 'bg-yellow-400' :
                                'bg-gray-300';
                    const designLabel = designStatus?.replace(/_/g, ' ') || 'waiting assignment';
                    const prodRecord = assignment.job_production?.find(p => {
                        if (p?.quote_id != null) {
                            return p.quote_id === qId
                        }
                        return p.quote_type === qt
                    });
                    const prodPhase = prodRecord?.phase || 'not_started';
                    const prodColor =
                        prodPhase === 'completed' ? 'bg-green-500' :
                            ['in_cutting', 'cutting_ended', 'in_production'].includes(prodPhase) ? 'bg-yellow-400' :
                                'bg-gray-300';
                    const prodLabel = prodPhase?.replace(/_/g, ' ') || 'not started';
                    const delivRecord = assignment.job_deliveries?.find(d => {
                        if (d?.child_id != null) {
                            return d.child_id === qId;
                        }
                        return d?.quote_type === qt;
                    });


                    const delivStatus = delivRecord?.delivery_status || 'not_scheduled';
                    const delivColor =
                        delivStatus === 'delivered' ? 'bg-green-500' :
                            delivStatus === 'cancelled' ? 'bg-red-500' :
                                ['scheduled', 'in_transit'].includes(delivStatus) ? 'bg-yellow-400' :
                                    'bg-gray-300';
                    const delivLabel = delivStatus?.replace(/_/g, ' ') || 'not scheduled';
                    return (
                        <div className="flex flex-row gap-2 w-full absolute top-0 left-0 px-2 pt-1 !cursor-pointer">
                            <div className={`w-full h-0.5 rounded-full ${designColor} ${designColor === "bg-yellow-400" ? "animate-pulse" : "animate-none"}`}>
                                <TextTooltip text={`Design Status: ${designLabel}`} customStyle="w-full h-full !inline">
                                    <div className="w-full h-1 -translate-y-[50%]" />
                                </TextTooltip>
                            </div>
                            <div className={`w-full h-0.5 rounded-full ${prodColor} ${prodColor === "bg-yellow-400" ? "animate-pulse" : "animate-none"}`}>
                                <TextTooltip text={`Production Status: ${prodLabel}`} customStyle="w-full h-full !inline">
                                    <div className="w-full h-1 -translate-y-[50%]" />
                                </TextTooltip>
                            </div>
                            <div className={`w-full h-0.5 rounded-full ${delivColor} ${delivColor === "bg-yellow-400" ? "animate-pulse" : "animate-none"}`}>
                                <TextTooltip text={`Delivery Status: ${delivLabel}`} customStyle="w-full h-full !inline">
                                    <div className="w-full h-1 -translate-y-[50%]" />
                                </TextTooltip>
                            </div>
                        </div>
                    );
                })()}

                <div className="flex items-start justify-between mt-1">
                    <div className={`flex items-center gap-2 min-w-0 flex-1`}>
                        <FiHash className="w-3 h-3 text-gray-700 flex-shrink-0" />
                        <TextTooltip text={assignment?.quote_info?.child_unique_id || assignment?.unique_id} >
                            <span className="font-semibold text-white text-sm truncate bg-gray-500 rounded px-1">
                                {assignment?.quote_info?.child_unique_id || assignment?.unique_id}
                            </span>
                        </TextTooltip>
                        <div className="flex items-center gap-1">
                            {assignment?.job_type === 'Timber'
                                ? <FiTruck className="w-3 h-3 text-amber-600 flex-shrink-0" />
                                : <UserPen className="w-3 h-3 text-blue-700 flex-shrink-0" />
                            }
                            {assignment?.job_type === 'Timber' ? (() => {
                                const timberDelivery = (assignment?.job_deliveries ?? []).find(d => d.quote_type === 'timber');
                                const driverName = timberDelivery?.assigned_driver_name;
                                return (
                                    <span className={`text-xs truncate ${driverName ? "text-amber-600 font-semibold" : "text-gray-500 font-semibold"}`}>
                                        {driverName || "N/A"}
                                    </span>
                                );
                            })() : (
                                <span className={`text-sm truncate  ${assignedDesigner ? "text-blue-700 font-semibold" : "text-gray-500 font-semibold"}`}>
                                    {assignedDesigner ? assignedDesigner?.name : "N/A"}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <MobileJobActions job={assignment} className="flex flex-col gap-2 absolute top-3 right-2">
                            <InfoTooltip aria-label="Job information" assignment={assignment} />
                            {isMobile && isAdmin && (
                                <button
                                    onClick={(e) => onMobileMove(assignment, e, playgroundMode)}
                                    type="button" aria-label="Move job" title="Move job" className="p-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 transition-all shadow-md"
                                >
                                    <FiMove className="w-3.5 h-3.5" />
                                </button>
                            )}
                            <QuickNotesButton aria-label="Quick notes"
                                assignment={assignment}
                                userProfile={userProfile}
                                isOpen={notesOpen}
                                onToggle={(val, pos) => setOpenNotesBubble(val ? { quoteId: assignment?.quote_info?.id, ...pos } : null)}
                                bubblePos={notesBubblePos}
                                setScheduleData={setScheduleData}
                                setUnscheduledAssignments={setUnscheduledAssignments}
                                setPlaygroundAssignments={setPlaygroundAssignments}
                            />
                            {assignment?.job_type === 'Timber' &&
                                <button
                                    aria-label={assignment?.quote_info?.timber_is_checked ? "Uncheck timber" : "Check timber"}
                                    onClick={(e) => { e.stopPropagation(); onToggleTimberChecked(assignment); }}
                                    className={`p-1.5 rounded-lg  hover:bg-gray-100 border transition-all shadow-md ${assignment?.quote_info?.timber_is_checked ? "bg-green-200 text-green-700 border-green-500" : "bg-white text-gray-600 border-gray-300"}`}
                                >
                                    {assignment?.quote_info?.timber_is_checked ?
                                        <SquareCheckBig className="w-3.5 h-3.5" />
                                        :
                                        <Square className="w-3.5 h-3.5" />
                                    }
                                </button>
                            }
                        </MobileJobActions>

                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <FiUser className="w-3 h-3 text-blue-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700 truncate font-medium">
                        {assignment.customer_name}
                    </span>
                </div>

                <div className="mb-2 flex flex-row items-center min-h-[20px]">
                    {assignment?.customer_address ? (
                        <p
                            // href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assignment?.customer_address)}`}
                            // target="_blank"
                            // rel="noopener noreferrer"
                            // onClick={(e) => e.stopPropagation()}
                            className="text-xs text-blue-700 flex items-center gap-1 mt-1"
                        >
                            <MapIcon className="w-3 h-3 mr-1" />
                            {assignment?.customer_address}
                        </p>
                    ) : (
                        <div className="mt-1"></div>
                    )}
                </div>

                <div className="flex items-center text-xs font-semibold justify-between border-t border-gray-100 pt-1">
                    {(() => {
                        if (assignment?.quote_info?.on_hold) {
                            return (
                                <span className="text-xs text-white px-1 py-0.5 font-semibold rounded truncate inline-block bg-gray-600">
                                    On Hold
                                </span>
                            );
                        }
                        if (assignment?.job_type === 'Timber') {
                            const timberDelivery = (assignment?.job_deliveries ?? []).find(d => d.quote_type === 'timber');
                            let label, color;
                            if (!timberDelivery || timberDelivery.delivery_status === 'not_scheduled') {
                                label = 'Ready For Delivery';
                                color = 'bg-emerald-500';
                            } else if (timberDelivery.delivery_status === 'delivered') {
                                label = 'Completed';
                                color = 'bg-emerald-700';
                            } else {
                                const deliveryColors = {
                                    scheduled: 'bg-amber-500',
                                    in_transit: 'bg-blue-500',
                                };
                                label = formatStatus(timberDelivery.delivery_status);
                                color = deliveryColors[timberDelivery.delivery_status] ?? 'bg-gray-500';
                            }
                            return (
                                <span className={`text-xs text-white px-1 py-0.5 font-semibold rounded truncate inline-block ${color}`}>
                                    {label}
                                </span>
                            );
                        }
                        return (
                            <span className={`text-xs text-white px-1 py-0.5 font-semibold rounded inline-block text-nowrap ${cardStatusStyle(assignment?.quote_info, "bar")}`}>
                                {getJobStatusSummary(assignment, "design") === "Waiting Assignment" ? "N/A" : getJobStatusSummary(assignment, "design")}
                            </span>
                        );
                    })()}
                    <div className="flex flex-row gap-1 flex-wrap w-fit justify-end self-end">
                        <span className={`self-center flex flex-row items-center gap-1 text-[10px] my-auto py-[1px] px-1.5 rounded uppercase ${INVOICE_STYLES[getInvoiceStatusText(assignment?.quote_info, company).toLowerCase().replaceAll(" ", "_")]}`}>
                            {getInvoiceSourceIcon(assignment?.quote_info, company)}
                            {getInvoiceStatusText(assignment?.quote_info, company)}
                        </span>
                        {assignment?.quote_info?.delivery_time_slot && (
                            <span className={`self-center text-[10px] my-auto py-[1px] px-1.5 rounded uppercase text-blue-800 bg-blue-200`}>
                                {assignment?.quote_info?.delivery_time_slot}
                            </span>
                        )}
                        {assignment?.quote_info?.layout_only && (
                            <span className="self-center text-[10px] my-auto py-[1px] px-1.5 rounded uppercase flex flex-row bg-purple-100 text-purple-600 items-center gap-1">
                                <PanelTop className="w-3 h-3" />
                                Layout
                            </span>
                        )}
                        {showWallQuoteIndicator && hasWallQuote && (

                            <TextTooltip text="This job has a wall quote">
                                <span className="self-center text-[10px] my-auto py-[2.5px] px-1 rounded uppercase flex flex-row bg-orange-100 border-orange-200 text-orange-700 items-center border gap-1">
                                    <BrickWall className="w-3 h-3 self-center my-auto" />
                                </span>
                            </TextTooltip>

                        )}
                        {needsSiteMeasure && !isMobile && (
                            <TextTooltip text={siteMeasureTooltip}>
                                <button
                                    type="button"
                                    onClick={(e) => e.stopPropagation()}
                                    className={`self-center text-[10px] my-auto py-[1px] px-1.5 rounded uppercase flex flex-row items-center gap-1 border transition-colors ${siteMeasureConfirmed
                                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                                        : 'bg-orange-100 text-orange-700 border-orange-200'
                                        }`}
                                >
                                    <MapIcon className="w-3 h-3" />
                                    SM
                                </button>
                            </TextTooltip>
                        )}
                    </div>
                </div>
            </div>
        </AssignmentContextMenu>
    );
});

// VirtualDroppableDay: combines useDroppable (dnd-kit) with useVirtualizer (@tanstack/react-virtual).
// Only visible cards are rendered in the DOM, but ALL ids live in SortableContext so dnd-kit
// can still compute sort order. This cuts DOM nodes from 300→~30 for 5-6 columns.
const VirtualDroppableDay = memo(function VirtualDroppableDay({
    dayKey, dayLabel, dayDate, dayIsPast, dayIsToday, dayHolidays,
    assignments, sortedIds, jobCount, completedCount, notAssignedCount, inDesignCount, isFiltered,
    renderCard, showSaturday
}) {
    const { isOver, setNodeRef } = useDroppable({ id: `day-${dayKey}` });
    const scrollRef = useRef(null);
    const isHoliday = dayHolidays.length > 0;

    // Merge dnd-kit's setNodeRef with our scrollRef for useVirtualizer
    const setRefs = useCallback((node) => {
        setNodeRef(node);
        scrollRef.current = node;
    }, [setNodeRef]);

    const virtualizer = useVirtualizer({
        count: assignments.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 156, // estimated card height + gap
        overscan: 3,
    });

    const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div
            ref={setRefs}
            className={`quote-schedule-day ${assignments.length === 0 ? 'is-empty' : ''} bg-gray-50 border-2 ${isOver ? 'border-blue-300 bg-blue-50'
                : isHoliday ? 'border-amber-400'
                    : dayIsPast ? 'border-gray-300 bg-gray-100/50'
                        : 'border-gray-200'
                } rounded-xl overflow-y-auto hover:border-blue-300 transition-colors h-[70vh] ${dayIsPast ? 'opacity-80' : ''}`}
        >
            {/* Sticky header — identical to DroppableDay */}
            <div className={`bg-gradient-to-r ${isHoliday ? 'from-amber-50 to-amber-100' : dayIsPast ? 'from-gray-100 to-gray-200' : 'from-gray-50 to-gray-100'} px-3 py-1 border-b ${isHoliday ? 'border-amber-200' : 'border-gray-200'} sticky top-0 z-10`}>
                <div className={`relative p-1 flex justify-between ${(windowWidth > (showSaturday ? 1755 : 1453)) ? 'flex-row' : 'flex-col'}`}>
                    <div className={`flex items-start justify-between`}>
                        <div className="flex-1 flex-col flex items-start gap-0.5">
                            <div className="flex flex-row gap-2 items-center">
                                <h3 className={`font-bold text-lg leading-none ${dayIsToday ? 'text-teal-700' : 'text-gray-800'}`}>
                                    {dayLabel}
                                </h3>
                                <p className="text-xs text-gray-500 font-medium">{dayDate}</p>
                            </div>

                            {dayIsToday && (
                                <span className="inline-flex mt-1 bg-green-500 text-white text-[10px] leading-none font-bold px-2 py-0.5 rounded-full shadow-md animate-pulse">
                                    TODAY
                                </span>
                            )}

                            {dayIsPast && !dayIsToday && (
                                <span className="inline-flex mt-1 bg-gray-400 text-white text-[10px] leading-none font-bold px-2 py-0.5 rounded-full shadow-md">
                                    PAST
                                </span>
                            )}

                            {isHoliday && (
                                <p className="text-xs mt-1 flex flex-row items-start text-amber-700 font-semibold truncate gap-1" title={dayHolidays.join(', ')}>
                                    <PartyPopper className="w-4 h-4" />
                                    <span>
                                        {dayHolidays[0]}{dayHolidays.length > 1 ? ` +${dayHolidays.length - 1}` : ''}
                                    </span>
                                </p>
                            )}
                        </div>
                    </div>
                    <div className={`flex items-center justify-center gap-1 ${(windowWidth > (showSaturday ? 1755 : 1453)) ? 'w-auto mt-0' : 'w-full mt-2'}`}>
                        <div className={`flex flex-col self-start ${(windowWidth > (showSaturday ? 1755 : 1453)) ? 'w-auto' : 'w-full'}`}>
                            <div
                                className={`flex items-center text-center justify-center rounded-md text-[10px] font-bold text-zinc-600 border-zinc-400 border px-2 mb-0.5`}
                            >
                                {`Total Jobs: ${jobCount}`}
                            </div>
                            <div className="flex flex-row gap-1 justify-between w-full">
                                <div
                                    className={`flex items-center justify-center text-[10px] font-bold text-zinc-500 border-zinc-400 border rounded-md px-1 w-full whitespace-nowrap`}
                                >
                                    {jobCount > 0 ? `N/A: ${notAssignedCount}` : '-'}
                                </div>
                                <div
                                    className={`flex items-center justify-center text-[10px] font-bold text-yellow-600 border-yellow-400 border rounded-md px-1 w-full whitespace-nowrap`}
                                >
                                    {jobCount > 0 ? `In Des: ${inDesignCount} ` : '-'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Virtualized card list */}
            <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
                {assignments.length > 0 ? (
                    <div
                        className="px-2 pt-2 relative"
                        style={{ height: `${virtualizer.getTotalSize()}px` }}
                    >
                        {virtualizer.getVirtualItems().map((vi) => (
                            <div
                                key={vi.key}
                                data-index={vi.index}
                                ref={virtualizer.measureElement}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    transform: `translateY(${vi.start}px)`,
                                    paddingLeft: 8,
                                    paddingRight: 8,
                                    paddingBottom: 8,
                                }}
                            >
                                {renderCard(assignments[vi.index])}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <FiCalendar className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">
                            {isFiltered ? 'No matches' : 'No assignments'}
                        </p>
                    </div>
                )}
            </SortableContext>
        </div>
    );
});

// VirtualUnscheduledGrid: virtualised grid for the unscheduled assignments panel.
// Renders cards in rows of COLS, keeping only visible rows in the DOM.
// ALL item ids are still passed to SortableContext so dnd-kit knows the full order.
const UNSCHEDULED_COLS = 3;
const VirtualUnscheduledGrid = function VirtualUnscheduledGrid({
    assignments, sortedIds, renderCard, isFiltered,
}) {
    const scrollRef = useRef(null);
    const rowCount = Math.ceil(assignments.length / UNSCHEDULED_COLS);

    const virtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 180, // card height (~160px) + gap (20px)
        overscan: 2,
    });

    if (assignments.length === 0) {
        return (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <FiPackage className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">
                    {isFiltered ? 'No matching unscheduled assignments' : 'All assignments have been scheduled'}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                    {isFiltered ? 'Try a different search term' : 'Great job!'}
                </p>
            </div>
        );
    }

    return (
        <SortableContext items={sortedIds} strategy={rectSortingStrategy}>
            <div
                ref={scrollRef}
                className="overflow-y-auto"
                style={{ maxHeight: '520px' }}
            >
                <div
                    className="relative"
                    style={{ height: `${virtualizer.getTotalSize()}px` }}
                >
                    {virtualizer.getVirtualItems().map((vRow) => {
                        const startIdx = vRow.index * UNSCHEDULED_COLS;
                        const rowItems = assignments.slice(startIdx, startIdx + UNSCHEDULED_COLS);
                        return (
                            <div
                                key={vRow.key}
                                data-index={vRow.index}
                                ref={virtualizer.measureElement}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    transform: `translateY(${vRow.start}px)`,
                                }}
                                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 pb-4"
                            >
                                {rowItems.map((a) => renderCard(a))}
                            </div>
                        );
                    })}
                </div>
            </div>
        </SortableContext>
    );
};

// VirtualPlaygroundGrid: virtualised grid for the playground (pinned cards) panel.
// No SortableContext because items are dragged OUT of the playground, not sorted within it.
const PLAYGROUND_COLS = 3;
const VirtualPlaygroundGrid = function VirtualPlaygroundGrid({
    assignments, renderCard,
}) {
    const scrollRef = useRef(null);
    const rowCount = Math.ceil(assignments.length / PLAYGROUND_COLS);

    const virtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 180,
        overscan: 2,
    });

    if (assignments.length === 0) {
        return (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <FiPackage className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">Drop assignments here</p>
                <p className="text-sm text-gray-400 mt-1">Drag any card into this area to pin it</p>
            </div>
        );
    }

    return (
        <div
            ref={scrollRef}
            className="overflow-y-auto"
            style={{ maxHeight: '520px' }}
        >
            <div
                className="relative"
                style={{ height: `${virtualizer.getTotalSize()}px` }}
            >
                {virtualizer.getVirtualItems().map((vRow) => {
                    const startIdx = vRow.index * PLAYGROUND_COLS;
                    const rowItems = assignments.slice(startIdx, startIdx + PLAYGROUND_COLS);
                    return (
                        <div
                            key={vRow.key}
                            data-index={vRow.index}
                            ref={virtualizer.measureElement}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${vRow.start}px)`,
                            }}
                            className="grid grid-cols-1 xl:grid-cols-3 gap-4 pb-4"
                        >
                            {rowItems.map((a) => renderCard(a))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const QuoteSchedule = ({
    onDuplicateJob = () => { },
}) => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const { companySlug, quoteType } = useParams();
    const isMobile = useMobileSchedule();

    const [editOpen, setEditOpen] = useState(false);
    const [editJobId, setEditJobId] = useState(null);

    const holidayMap = useHolidays(company?.state);

    // New week offset system - 0 is current week, -1 is last week, 1 is next week, etc.
    const [weekOffset, setWeekOffset] = useState(() => {
        try { const v = localStorage.getItem('quoteSchedule_weekOffset'); return v !== null ? JSON.parse(v) : 0; } catch { return 0; }
    });
    useEffect(() => { weekOffsetRef.current = weekOffset; }, [weekOffset]);
    const [scheduleData, setScheduleData] = useState({});
    const [unscheduledAssignments, setUnscheduledAssignments] = useState([]);
    // cardOrder tracks the manual sort order for each day: { Monday: [id, id, ...], ... }
    const [cardOrder, setCardOrder] = useState({});
    // unscheduledOrder tracks the manual sort order for the unscheduled section: [id, id, ...]
    const [unscheduledOrder, setUnscheduledOrder] = useState([]);
    // Suppress sort re-ordering from our own subscription echo after a local drag
    const reorderLockRef = useRef(false);
    // True while a drag gesture is active — subscription handlers check this and bail
    // immediately so that incoming Supabase events never trigger setState during drag.
    const isDraggingRef = useRef(false);
    // Refs for synchronous reads inside subscription handlers
    const scheduleDataRef = useRef({});
    const unscheduledAssignmentsRef = useRef([]);
    const weekOffsetRef = useRef(0);
    const fetchPlaygroundRef = useRef(null);
    const waitingPanelRef = useRef(null);
    // Always-current snapshot for real-time handlers (avoids stale closure reads)
    const latestRef = useRef({});
    // Tracks quote IDs of moves we initiated locally so we skip our own subscription echo
    const selfMovedRef = useRef(new Set());

    const [activeId, setActiveId] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingMove, setPendingMove] = useState(null);
    const [showOrderWarningDetails, setShowOrderWarningDetails] = useState(false);
    const [pastDateWarning, setPastDateWarning] = useState(false);

    // Mobile-specific states
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [mobileViewMode, setMobileViewMode] = useState('grid');
    const [selectedDay, setSelectedDay] = useState(null);

    // Date picker modal
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [datePickerValue, setDatePickerValue] = useState(null); // Track the actual selected date in the picker

    // Drag to date picker states
    const [showDragDatePicker, setShowDragDatePicker] = useState(false);
    const [draggedForDatePicker, setDraggedForDatePicker] = useState(null);
    const [selectedDateFromPicker, setSelectedDateFromPicker] = useState(null);

    // Drag and drop cache
    const [draggedItemCache, setDraggedItemCache] = useState(null);

    const [showOnlyMine, setShowOnlyMine] = useState(false);
    const [selectedUser, setSelectedUser] = useState('');
    const [availableUsers, setAvailableUsers] = useState({ designers: [], estimators: [] });
    const [designStatusFilter, setDesignStatusFilter] = useState('all');
    const [cancelledFilter, setCancelledFilter] = useState(() => {
        try {
            return localStorage.getItem('quoteSchedule_cancelledFilter') || 'active_only';
        } catch {
            return 'active_only';
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('quoteSchedule_cancelledFilter', cancelledFilter);
        } catch { }
    }, [cancelledFilter]);
    const [showSaturday, setShowSaturday] = useState(() => {
        try {
            const stored = localStorage.getItem('quoteSchedule_showSaturday');
            return stored !== null ? JSON.parse(stored) : true;
        } catch {
            return true;
        }
    });

    const [showMonthlyInvoices, setShowMonthlyInvoices] = useState(() => {
        try {
            const stored = localStorage.getItem('quoteSchedule_showMonthlyInvoices');
            return stored !== null ? JSON.parse(stored) : false;
        } catch {
            return false;
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(
                'quoteSchedule_showMonthlyInvoices',
                JSON.stringify(showMonthlyInvoices)
            );
        } catch { }
    }, [showMonthlyInvoices]);


    const [isLoading, setIsLoading] = useState(false);

    const [searchQuery, setSearchQuery] = useState(() => {
        try { return JSON.parse(localStorage.getItem('quoteSchedule_searchQuery') ?? '""'); } catch { return ''; }
    });
    const [isScrolled, setIsScrolled] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const dropAnimationConfig = {
        duration: 200,
        easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: {
                    opacity: '0.5',
                },
            },
        }),
    };

    // Playground: separate state — persists regardless of which week is visible.
    const [playgroundAssignments, setPlaygroundAssignments] = useState([]);
    // Which card's quick-notes bubble is open + its position
    // null = closed; { quoteId, x, y, showAbove, flipLeft } = open
    const [openNotesBubble, setOpenNotesBubble] = useState(null);

    useEffect(() => {
        return () => {
            setActiveId(null);
        };
    }, []);

    // Keep refs in sync with state (for synchronous reads in subscription handlers)
    useEffect(() => { scheduleDataRef.current = scheduleData; }, [scheduleData]);
    useEffect(() => { unscheduledAssignmentsRef.current = unscheduledAssignments; }, [unscheduledAssignments]);

    // Keep cardOrder in sync with scheduleData.
    // When the lock is active (local user just dragged), skip re-sorting to avoid
    // overwriting the optimistic local order before the DB echo settles.
    // When the lock is inactive (remote update), re-sort by sort_order to reflect
    // other users' changes immediately.
    useEffect(() => {
        setCardOrder(prev => {
            const next = { ...prev };
            for (const [day, assignments] of Object.entries(scheduleData)) {
                // Use quote_info?.id as the unique row key (unique per quote even for multi-quote jobs)
                const rowId = (a) => a.quote_info?.id || a.id;
                const incomingIds = assignments.map(rowId);
                const existing = prev[day] || [];

                const sortOrderMap = {};
                assignments.forEach(a => {
                    sortOrderMap[rowId(a)] = a.quote_info?.sort_order ?? null;
                });

                const preserved = existing.filter(id => incomingIds.includes(id));
                const newIds = incomingIds.filter(id => !existing.includes(id));
                const merged = [...preserved, ...newIds];

                // Only re-sort if the lock isn't active (i.e., remote change, not our own drag echo)
                if (!reorderLockRef.current) {
                    merged.sort((a, b) => {
                        const aO = sortOrderMap[a] ?? null;
                        const bO = sortOrderMap[b] ?? null;
                        if (aO === null && bO === null) return 0;
                        if (aO === null) return 1;
                        if (bO === null) return -1;
                        return aO - bO;
                    });
                }

                next[day] = merged;
            }
            return next;
        });
    }, [scheduleData]);

    // Keep unscheduledOrder in sync with unscheduledAssignments.
    // Same lock-aware strategy as cardOrder.
    useEffect(() => {
        setUnscheduledOrder(prev => {
            // Use quote_info?.id as the unique row key (unique per quote even for multi-quote jobs)
            const rowId = (a) => a.quote_info?.id || a.id;
            const incomingIds = unscheduledAssignments.map(rowId);

            const sortOrderMap = {};
            unscheduledAssignments.forEach(a => {
                sortOrderMap[rowId(a)] = a.quote_info?.sort_order ?? null;
            });

            const preserved = prev.filter(id => incomingIds.includes(id));
            const newIds = incomingIds.filter(id => !prev.includes(id));
            const merged = [...preserved, ...newIds];

            if (!reorderLockRef.current) {
                merged.sort((a, b) => {
                    const aO = sortOrderMap[a] ?? null;
                    const bO = sortOrderMap[b] ?? null;
                    if (aO === null && bO === null) return 0;
                    if (aO === null) return 1;
                    if (bO === null) return -1;
                    return aO - bO;
                });
            }

            return merged;
        });
    }, [unscheduledAssignments]);

    useEffect(() => {
        if (company?.id && quoteType) {
            fetchAssignments();
        }
    }, [company, quoteType, showOnlyMine, selectedUser, weekOffset, designStatusFilter, cancelledFilter]);

    // Playground is independent of week/filters — fetch once on mount and after company/type changes
    useEffect(() => {
        if (company?.id && quoteType) {
            fetchPlaygroundAssignments();
        }
    }, [company?.id, quoteType]);

    // Fetch available users for filtering
    useEffect(() => {
        if (company?.id && (userProfile?.role === 'admin' || userProfile?.role === 'super_admin')) {
            fetchAvailableUsers();
        }
    }, [userProfile, company]);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 200);
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        try { localStorage.setItem('quoteSchedule_showSaturday', JSON.stringify(showSaturday)); } catch { }
    }, [showSaturday]);

    useEffect(() => {
        try { localStorage.setItem('quoteSchedule_searchQuery', JSON.stringify(searchQuery)); } catch { }
    }, [searchQuery]);

    useEffect(() => {
        try { localStorage.setItem('quoteSchedule_weekOffset', JSON.stringify(weekOffset)); } catch { }
    }, [weekOffset]);

    const fetchAvailableUsers = async () => {
        if (!company?.id) return;
        try {
            const users = await getAvailableUsers(company.id);
            setAvailableUsers(users);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchPlaygroundAssignments = async () => {
        if (!company?.id || !quoteType) return;
        const { data } = await getQuotePlaygroundAssignments(company.id, quoteType);
        setPlaygroundAssignments(data || []);
    };
    fetchPlaygroundRef.current = fetchPlaygroundAssignments;
    // Sync latestRef on every render so subscription handlers always read current values
    latestRef.current = { scheduleData, unscheduledAssignments, playgroundAssignments, weekOffset };

    const fetchAssignments = async () => {
        try {
            setIsLoading(true);

            // Pass the current week start date to the fetch functions
            const weekStart = getCurrentWeekStart();

            const [scheduledRes, unscheduledRes] = await Promise.all([
                getScheduleAssignments(company.id, quoteType, {
                    showOnlyMine: showOnlyMine ? userProfile.id : null,
                    userId: selectedUser || null,
                    weekStart: weekStart,
                    designStatusFilter: designStatusFilter !== 'all' ? designStatusFilter : null,
                    cancelledFilter,
                }),
                getUnscheduledByQuote(company.id, quoteType, {
                    showOnlyMine: showOnlyMine ? userProfile.id : null,
                    userId: selectedUser || null,
                    designStatusFilter: designStatusFilter !== 'all' ? designStatusFilter : null,
                    cancelledFilter,
                })
            ]);

            if (scheduledRes.error || unscheduledRes.error) {
                throw scheduledRes.error || unscheduledRes.error;
            }

            scheduleDataRef.current = scheduledRes.data || {};
            unscheduledAssignmentsRef.current = unscheduledRes.data || [];
            setScheduleData(scheduledRes.data || {});
            setUnscheduledAssignments(unscheduledRes.data || []);
        } catch (error) {
            console.error('Error fetching assignments:', error);
            setScheduleData({});
            setUnscheduledAssignments([]);
        } finally {
            setIsLoading(false);
        }
    };

    const insertQuoteCardLocally = useCallback((inserted) => {
        if (!inserted?.id) return;
        if (inserted.quote_type !== quoteType) return;

        const getTargetDayKey = (dateStr) => {
            if (!dateStr) return null;

            const today = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
            const viewMonday = addWeeks(startOfWeek(today, { weekStartsOn: 1 }), weekOffsetRef.current);

            const jobDate = new Date(dateStr);
            const diffDays = Math.floor((jobDate - viewMonday) / (1000 * 60 * 60 * 24));

            if (diffDays >= 0 && diffDays <= 6) {
                return jobDate.toLocaleDateString('en-US', { weekday: 'long' });
            }

            return null;
        };

        const matchesFilters = () => {
            if (designStatusFilter !== 'all') {
                const status = inserted.design_status || 'waiting_assignment';
                if (status !== designStatusFilter) return false;
            }

            return true;
        };

        if (!matchesFilters()) return;

        const cardId = inserted.id;
        const targetDayKey = getTargetDayKey(
            inserted.expected_delivery_date || inserted.expected_finish_date
        );

        const newCard = {
            id: inserted.job_id,
            unique_id: '',
            customer_name: '',
            customer_number: '',
            customer_mail: '',
            carpenter_supervisor_name: '',
            carpenter_supervisor_number: '',
            customer_address: '',
            job_type: '',
            main_status: null,
            current_status: null,
            invoiced: null,
            quote_sent: null,
            booking_delivery_date: null,
            expected_estimation_finish_date: null,
            updated_at: inserted.updated_at || null,
            on_hold: false,
            urgent_quote: false,
            urgent_design: false,

            job_quotes: [],
            job_designers: [],
            job_estimators: null,
            job_production: [],
            job_deliveries: [],

            quote_info: {
                id: inserted.id,
                quoteId: inserted.id,
                job_id: inserted.job_id,
                quote_type: inserted.quote_type,
                child_unique_id: inserted.child_unique_id || null,
                expected_delivery_date: inserted.expected_delivery_date || null,
                expected_finish_date: inserted.expected_finish_date || null,
                design_status: inserted.design_status || 'waiting_assignment',
                estimation_status: inserted.estimation_status || null,
                production_status: inserted.production_status || null,
                invoice_state: inserted.invoice_state || null,
                layout_only: inserted.layout_only || false,
                on_hold: inserted.on_hold || false,
                delivery_time_slot: inserted.delivery_time_slot || null,
                timber_is_checked: inserted.timber_is_checked || false,
                quick_notes: inserted.quick_notes || [],
                quote_playground: inserted.quote_playground || false,
                delivery_playground: inserted.delivery_playground || false,
                is_required: inserted.is_required ?? true,
                sent_production: inserted.sent_production || false,
                waiting_job: inserted.waiting_job || false,
                sort_order: inserted.sort_order ?? null,
            },

            schedule_row_id: inserted.child_unique_id || null,
        };

        const isInPlayground =
            inserted.quote_playground === true ||
            inserted.delivery_playground === true;

        setScheduleData(prev => {
            const next = {};
            for (const dayKey in prev) {
                next[dayKey] = prev[dayKey].filter(item => (item.quote_info?.id || item.id) !== cardId);
            }

            if (targetDayKey && !isInPlayground) {
                next[targetDayKey] = [...(next[targetDayKey] || []), newCard];
            }

            return next;
        });

        setUnscheduledAssignments(prev => {
            const cleaned = prev.filter(item => (item.quote_info?.id || item.id) !== cardId);

            if (!targetDayKey && !isInPlayground) {
                return [...cleaned, newCard];
            }

            return cleaned;
        });

        setPlaygroundAssignments(prev => {
            const cleaned = prev.filter(item => (item.quote_info?.id || item.id) !== cardId);

            if (isInPlayground) {
                return [...cleaned, newCard];
            }

            return cleaned;
        });
    }, [quoteType, designStatusFilter, cancelledFilter]);

    // Real-time subscriptions
    useEffect(() => {
        if (!company?.id) return;

        // scheduleRowId (= child_unique_id) targets a specific multi-quote row when provided.
        // Without it, all rows for the job_id are updated (correct for job-level field patches).
        const updateAssignment = (id, updater, scheduleRowId = null) => {
            const matcher = (a) => scheduleRowId
                ? a.schedule_row_id === scheduleRowId
                : a.id === id;
            setScheduleData(prev => {
                const next = { ...prev };
                for (const dateKey of Object.keys(next)) {
                    next[dateKey] = next[dateKey].map(a => matcher(a) ? updater(a) : a);
                }
                return next;
            });
            setUnscheduledAssignments(prev => prev.map(a => matcher(a) ? updater(a) : a));
        };

        // Handle job table updates
        const handleJobUpdate = (payload) => {
            if (isDraggingRef.current) return;
            if (payload.eventType === 'DELETE') {
                setScheduleData(prev => {
                    const next = { ...prev };
                    for (const dateKey of Object.keys(next)) {
                        next[dateKey] = next[dateKey].filter(a => a.id !== payload.old.id);
                    }
                    return next;
                });
                setUnscheduledAssignments(prev => prev.filter(a => a.id !== payload.old.id));
                return;
            }

            if (payload.eventType === 'INSERT') {
                // Do NOT refresh here — the companion job_quotes INSERT fires
                // immediately after and handleQuotesUpdate will handle it,
                // but only when the quote_type matches the current view.
                // This prevents a full refresh for every new job regardless of quote type.
                return;
            }

            if (payload.eventType === 'UPDATE') {
                updateAssignment(payload.new.id, a => ({
                    ...a,
                    unique_id: payload.new.unique_id,
                    customer_name: payload.new.customer_name,
                    customer_number: payload.new.customer_number,
                    customer_mail: payload.new.customer_mail,
                    carpenter_supervisor_name: payload.new.carpenter_supervisor_name,
                    carpenter_supervisor_number: payload.new.carpenter_supervisor_number,
                    customer_address: payload.new.customer_address,
                    job_type: payload.new.job_type,
                    main_status: payload.new.main_status,
                    current_status: payload.new.current_status,
                    invoiced: payload.new.invoiced,
                    quote_sent: payload.new.quote_sent,
                    booking_delivery_date: payload.new.booking_delivery_date,
                    expected_estimation_finish_date: payload.new.expected_estimation_finish_date,
                    updated_at: payload.new.updated_at,
                    on_hold: payload.new.on_hold,
                    urgent_quote: payload.new.urgent_quote,
                    urgent_design: payload.new.urgent_design,
                }));
            }
        };

        // Handle job_designers table updates
        const handleDesignersUpdate = (payload) => {
            if (isDraggingRef.current) return;
            const { new: newData, old: oldData } = payload;

            if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
                // Update the job-level job_designers array on ALL rows for this job
                // (filtering per-quote row happens at render time via assignment.designers)
                updateAssignment(newData.job_id, a => {
                    const existing = (a.job_designers || []).filter(d => d.id !== newData.id);
                    return {
                        ...a,
                        job_designers: [...existing, {
                            id: newData.id,
                            user_id: newData.user_id,
                            name: newData.name,
                            quote_type: newData.quote_type,
                            child_job_id: newData.child_job_id || null,
                            is_started: newData.is_started,
                            is_completed: newData.is_completed
                        }]
                    };
                });
            }

            if (payload.eventType === 'DELETE') {
                updateAssignment(oldData.job_id, a => ({
                    ...a,
                    job_designers: (a.job_designers || []).filter(d => d.id !== oldData.id)
                }));
            }
        };

        // Handle job_estimators table updates
        const handleEstimatorsUpdate = (payload) => {
            if (isDraggingRef.current) return;
            const { new: newData, old: oldData } = payload;

            if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
                updateAssignment(newData.job_id, a => ({
                    ...a,
                    job_estimators: { id: newData.id, user_id: newData.user_id, name: newData.name, is_completed: newData.is_completed, is_started: newData.is_started }
                }));
            }

            if (payload.eventType === 'DELETE') {
                updateAssignment(oldData.job_id, a => ({ ...a, job_estimators: null }));
            }
        };

        // Handle job_quotes table updates
        const handleQuotesUpdate = (payload) => {
            if (isDraggingRef.current) return;
            if (payload.eventType === 'DELETE') return;

            // INSERT: a brand-new job_quotes row just appeared.
            // We can only fetch safely if this quote belongs to the current view's
            // quote_type — otherwise we'd refresh the roof schedule just because
            // someone created a wall job.
            if (payload.eventType === 'INSERT') {
                insertQuoteCardLocally(payload.new);
                return;
            }

            // Below: UPDATE-only logic

            const updated = payload.new;

            // Always patch nested job_quotes on ALL visible rows for this job.
            // This is needed even when updated.quote_type !== current page quoteType,
            // because warning checks depend on related wall/floor/roof quote dates.
            const patchNestedJobQuotes = (updatedQuote) => {
                const patchItem = (item) => {
                    if (item.id !== updatedQuote.job_id) return item;

                    const existingQuotes = item.job_quotes || [];
                    const exists = existingQuotes.some(q => q.id === updatedQuote.id);

                    return {
                        ...item,
                        job_quotes: exists
                            ? existingQuotes.map(q =>
                                q.id === updatedQuote.id
                                    ? { ...q, ...updatedQuote }
                                    : q
                            )
                            : [...existingQuotes, updatedQuote],
                    };
                };

                setScheduleData(prev => {
                    const next = { ...prev };
                    for (const dayKey of Object.keys(next)) {
                        next[dayKey] = next[dayKey].map(patchItem);
                    }
                    return next;
                });

                setUnscheduledAssignments(prev => prev.map(patchItem));

                setPlaygroundAssignments(prev => prev.map(patchItem));
            };

            patchNestedJobQuotes(updated);

            // ── Guard: only process updates that belong to THIS schedule's quote_type ──
            // Without this, a wall-quote update arrives on the roof schedule, finds no
            // matching card (matchesItem filters by quote_type), builds a skeleton card
            // from the fallback `{ id, quote_info: {} }`, then inserts that skeleton
            // into a day column or the unscheduled list because alreadyExists() returns
            // false.  Cross-type updates have zero business touching our state.
            if (updated.quote_type !== quoteType) return;

            const getItemId = (item) => item.quote_info?.id || item.id;
            const updatedId = updated.id || updated.job_id;

            const matchesItem = (item) => {
                if (updated.child_unique_id) {
                    return item.schedule_row_id === updated.child_unique_id;
                }
                return (
                    item.id === updated.job_id &&
                    item.quote_info?.quote_type === updated.quote_type
                );
            };

            const alreadyExists = (list) =>
                list?.some(item => getItemId(item) === updatedId);

            const patchedQuoteInfo = (existing = {}) => ({
                ...existing,
                ...updated
            });

            const {
                scheduleData: currentSchedule,
                unscheduledAssignments: currentUnscheduled,
                playgroundAssignments: currentPlayground,
            } = latestRef.current;

            const getTargetDayKey = (dateStr) => {
                if (!dateStr) return null;

                const today = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
                const viewMonday = addWeeks(startOfWeek(today, { weekStartsOn: 1 }), weekOffsetRef.current);

                const jobDate = new Date(dateStr);
                const diffDays = Math.floor((jobDate - viewMonday) / (1000 * 60 * 60 * 24));

                if (diffDays >= 0 && diffDays <= 6) {
                    return jobDate.toLocaleDateString('en-US', { weekday: 'long' });
                }

                return null;
            };

            // ─────────────────────────────────────────────
            // 🔍 Find existing card
            let existingCard = null;
            let existingLocation = null;

            for (const dayKey in currentSchedule) {
                const found = currentSchedule[dayKey].find(matchesItem);
                if (found) {
                    existingCard = found;
                    existingLocation = { type: 'schedule', dayKey };
                    break;
                }
            }

            if (!existingCard) {
                const unscheduledCard = currentUnscheduled.find(matchesItem) || null;
                if (unscheduledCard) {
                    existingCard = unscheduledCard;
                    existingLocation = { type: 'unscheduled' };
                }
            }

            if (!existingCard) {
                const playgroundCard = currentPlayground?.find(matchesItem) || null;
                if (playgroundCard) {
                    existingCard = playgroundCard;
                    existingLocation = { type: 'playground' };
                }
            }

            // ─────────────────────────────────────────────
            // 🧱 Build updated card (or fallback rebuild)
            //
            // If the job is NOT currently in our state at all (existingCard === null)
            // it means it either lives in a different week or didn't match our filters.
            // In that case we must NOT insert a skeleton — we do a full fetch instead
            // so the card arrives with all its real data (customer_name, unique_id, etc.).
            if (!existingCard) {
                return;
                // Only bother fetching if the update would actually land in our view
                // (date falls in current week) or turns the card into an unscheduled one.
                const wouldBeVisible =
                    !!(updated.expected_delivery_date || updated.expected_finish_date)
                        ? !!getTargetDayKey(updated.expected_delivery_date || updated.expected_finish_date)
                        : true; // no date = unscheduled = always visible
                if (wouldBeVisible) fetchAssignments();
                return;
            }

            const baseCard = existingCard;
            const nextTargetDayKey = getTargetDayKey(
                updated.expected_delivery_date || updated.expected_finish_date
            );

            const updatedCard = {
                ...baseCard,
                quote_info: patchedQuoteInfo(baseCard.quote_info)
            };

            const isInPlayground =
                updated.quote_playground === true ||
                updated.delivery_playground === true;

            const nextLocation = isInPlayground
                ? { type: 'playground' }
                : nextTargetDayKey
                    ? { type: 'schedule', dayKey: nextTargetDayKey }
                    : (!updated.expected_delivery_date && !updated.expected_finish_date)
                        ? { type: 'unscheduled' }
                        : { type: 'out-of-week' };

            const locationUnchanged =
                existingLocation?.type === nextLocation.type &&
                (existingLocation?.type !== 'schedule' || existingLocation?.dayKey === nextLocation.dayKey);

            if (locationUnchanged) {
                if (nextLocation.type === 'schedule') {
                    setScheduleData(prev => ({
                        ...prev,
                        [nextLocation.dayKey]: (prev[nextLocation.dayKey] || []).map(item =>
                            matchesItem(item) ? updatedCard : item
                        )
                    }));
                } else if (nextLocation.type === 'unscheduled') {
                    setUnscheduledAssignments(prev => prev.map(item =>
                        matchesItem(item) ? updatedCard : item
                    ));
                } else if (nextLocation.type === 'playground') {
                    setPlaygroundAssignments(prev => prev.map(item =>
                        matchesItem(item) ? updatedCard : item
                    ));
                }
                return;
            }

            // ─────────────────────────────────────────────
            // 🧹 Remove from everywhere (idempotent)
            setScheduleData(prev => {
                const next = {};
                for (const dayKey in prev) {
                    next[dayKey] = prev[dayKey].filter(item => !matchesItem(item));
                }
                return next;
            });

            setUnscheduledAssignments(prev =>
                prev.filter(item => !matchesItem(item))
            );

            // ─────────────────────────────────────────────
            // 🚨 Playground
            setPlaygroundAssignments(prev =>
                prev.filter(item => !matchesItem(item))
            );

            if (
                updated.quote_playground === true ||
                updated.delivery_playground === true
            ) {
                fetchPlaygroundRef.current?.();
                return;
            }

            // ─────────────────────────────────────────────
            // 📅 Decide destination
            const targetDayKey = getTargetDayKey(
                updated.expected_delivery_date || updated.expected_finish_date
            );

            // ─────────────────────────────────────────────
            // 📍 Reinsert safely (NO duplicates)

            if (targetDayKey) {
                setScheduleData(prev => {
                    const list = prev[targetDayKey] || [];

                    if (alreadyExists(list)) return prev;

                    return {
                        ...prev,
                        [targetDayKey]: [...list, updatedCard]
                    };
                });

            } else if (!updated.expected_delivery_date && !updated.expected_finish_date) {
                // ✅ UNSCHEDULED CASE (this was your bug area)
                setUnscheduledAssignments(prev => {
                    if (alreadyExists(prev)) return prev;
                    return [...prev, updatedCard];
                });

            } else {
                // out of current week → do nothing
            }
        };

        const jobSubscription = subscribeToJobUpdates(company.id, handleJobUpdate);
        const designersSubscription = subscribeToJobDesignersUpdates(company.id, handleDesignersUpdate);
        const estimatorsSubscription = subscribeToJobEstimatorsUpdates(company.id, handleEstimatorsUpdate);
        const quotesSubscription = subscribeToJobQuotesUpdates(company.id, handleQuotesUpdate);

        return () => {
            unsubscribeFromJobUpdates(jobSubscription);
            unsubscribeFromJobUpdates(designersSubscription);
            unsubscribeFromJobUpdates(estimatorsSubscription);
            unsubscribeFromJobUpdates(quotesSubscription);
        };
    }, [company?.id, quoteType]);

    // ── Search helpers ────────────────────────────────────────────────────────
    const useDebounce = (value, delay) => {
        const [debouncedValue, setDebouncedValue] = useState(value);
        useEffect(() => {
            const handler = setTimeout(() => setDebouncedValue(value), delay);
            return () => clearTimeout(handler);
        }, [value, delay]);
        return debouncedValue;
    };

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    const matchesSearch = (assignment) => {
        if (!debouncedSearchQuery.trim()) return true;
        const query = debouncedSearchQuery.toLowerCase();
        return (
            assignment.unique_id?.toLowerCase().includes(query) ||
            assignment.schedule_row_id?.toLowerCase().includes(query) ||
            assignment.customer_name?.toLowerCase().includes(query) ||
            assignment.customer_address?.toLowerCase().includes(query) ||
            assignment.carpenter_supervisor_name?.toLowerCase().includes(query) ||
            assignment.job_designers?.some(d => d.name?.toLowerCase().includes(query))
        );
    };

    const filterAssignments = (assignments) => {
        if (!debouncedSearchQuery.trim()) return assignments;
        return assignments.filter(matchesSearch);
    };

    // Helper functions
    const getAustralianDate = () => {
        return new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
    };

    // Get the start of the week based on offset
    const getCurrentWeekStart = () => {
        const today = getAustralianDate();
        const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 }); // Monday
        return addWeeks(currentWeekStart, weekOffset);
    };

    const jumpToWeek = (targetWeekStart) => {
        const today = getAustralianDate();
        const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
        const targetStart = startOfWeek(targetWeekStart, { weekStartsOn: 1 });

        const offset = differenceInWeeks(targetStart, currentWeekStart);

        setWeekOffset(offset);

        requestAnimationFrame(() => {
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        });
    };

    // Get week label for display
    const getWeekLabel = () => {
        const weekStart = getCurrentWeekStart();
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
        return `${format(weekStart, 'dd MMM')} – ${format(weekEnd, 'dd MMM yyyy')}`;
    };

    // Get month/year label for the current week
    const getMonthLabel = () => {
        const weekStart = getCurrentWeekStart();
        return format(weekStart, 'MMMM yyyy');
    };

    // Get date for specific day index
    const getDayDate = (dayIndex) => {
        const weekStart = getCurrentWeekStart();
        const targetDate = addDays(weekStart, dayIndex);
        return format(targetDate, 'dd/MM/yyyy');
    };

    // Get date object for specific day index
    const getDayDateObject = (dayIndex) => {
        const weekStart = getCurrentWeekStart();
        const targetDate = addDays(weekStart, dayIndex);

        const utcTargetDate = new Date(Date.UTC(
            targetDate.getFullYear(),
            targetDate.getMonth(),
            targetDate.getDate(),
            12, 0, 0, 0
        ));

        return utcTargetDate;
    };

    // Returns "YYYY-MM-DD" string for a Date — used as holiday map key
    const toDateKey = (date) => {
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    // Returns array of holiday names for a given Date, or empty array
    const getHolidaysForDate = (date) => {
        return holidayMap.get(toDateKey(date)) || [];
    };

    // Check if a day is today
    const isDayToday = (dayIndex) => {
        const weekStart = getCurrentWeekStart();
        const targetDate = addDays(weekStart, dayIndex);
        return isToday(targetDate);
    };

    // Check if a date is in the past
    const isDateInPast = (date) => {
        const today = startOfDay(getAustralianDate());
        return isBefore(startOfDay(date), today);
    };

    // Handle date picker selection
    const handleDatePickerSelect = (date) => {
        if (!date) return;

        const today = getAustralianDate();
        const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
        const selectedWeekStart = startOfWeek(date, { weekStartsOn: 1 });

        // Calculate the week offset
        const offset = differenceInWeeks(selectedWeekStart, currentWeekStart);
        setWeekOffset(offset);
        setShowDatePicker(false);

        // Clear the date picker value after selection to prevent highlighting across months
        setDatePickerValue(null);
    };

    // Initialize date picker value when opening
    const handleOpenDatePicker = () => {
        setDatePickerValue(getCurrentWeekStart());
        setShowDatePicker(true);
    };

    // Get assignment count for a specific date
    const getAssignmentCountForDate = (date) => {
        if (!scheduleData) return 0;

        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
        const weekStart = getCurrentWeekStart();

        // Check if this date is in the current displayed week
        const dateWeekStart = startOfWeek(date, { weekStartsOn: 1 });
        if (isSameWeek(dateWeekStart, weekStart, { weekStartsOn: 1 })) {
            return scheduleData[dayOfWeek]?.length || 0;
        }

        return 0;
    };

    // Get day class name based on assignment count + holidays
    const getDayClassName = (date) => {
        const count = getAssignmentCountForDate(date);
        const isHoliday = getHolidaysForDate(date).length > 0;
        const holidayClass = isHoliday ? 'react-datepicker__day--holiday' : '';

        let countClass = '';
        if (count >= 1 && count <= 3) countClass = 'react-datepicker__day--highlighted-green';
        else if (count > 3 && count <= 6) countClass = 'react-datepicker__day--highlighted-yellow';
        else if (count > 6) countClass = 'react-datepicker__day--highlighted-red';

        return [countClass, holidayClass].filter(Boolean).join(' ');
    };

    // Renders day cell content — wraps in TextTooltip when the day is a public holiday.
    // The inner span fills the full 2.5rem × 2.5rem cell so the tooltip triggers anywhere
    // in the cell, not just when hovering directly over the number.
    const renderDayContents = (day, date) => {
        const holidays = getHolidaysForDate(date);
        if (holidays.length === 0) return <>{day}</>;
        return (
            <TextTooltip text={holidays.join(' · ')}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '2.5rem', height: '2.5rem' }}>
                    {day}
                </span>
            </TextTooltip>
        );
    };

    // Filter function to disable Sundays (filterDate expects true for enabled dates)
    const isDateDisabled = (date) => {
        return date.getDay() !== 0; // 0 = Sunday, so return true for all other days
    };

    // Custom day class for drag date picker to only highlight exact date match
    const getDragDatePickerDayClass = (date) => {
        const isHoliday = getHolidaysForDate(date).length > 0;
        const holidayClass = isHoliday ? 'react-datepicker__day--holiday' : '';

        if (!selectedDateFromPicker) return holidayClass;

        const isSameDate = date.getDate() === selectedDateFromPicker.getDate() &&
            date.getMonth() === selectedDateFromPicker.getMonth() &&
            date.getFullYear() === selectedDateFromPicker.getFullYear();

        return [isSameDate ? 'react-datepicker__day--selected' : '', holidayClass].filter(Boolean).join(' ');
    };

    // Handle date picker selection during drag
    const handleDragDatePickerSelect = (date) => {
        if (!date || !draggedForDatePicker) return;

        // Store just the date for drop processing, but keep it in state for the Schedule button
        setSelectedDateFromPicker(date);
    };

    // Handle drop on date picker day
    const handleDropOnDatePickerDay = () => {
        if (!selectedDateFromPicker || !draggedForDatePicker) return;

        // Create a new date in UTC to preserve the selected date
        const localDate = new Date(selectedDateFromPicker);
        const newDate = new Date(Date.UTC(
            localDate.getFullYear(),
            localDate.getMonth(),
            localDate.getDate(),
            10, 0, 0, 0
        ));

        const isPast = isDateInPast(newDate);

        setPendingMove({
            assignment: draggedForDatePicker,
            newDate: newDate,
            dayKey: selectedDateFromPicker.toLocaleDateString('en-US', { weekday: 'long' }),
            quoteType: quoteType,
            isPastDate: isPast
        });

        setPastDateWarning(isPast);
        setShowDragDatePicker(false);
        setDraggedForDatePicker(null);
        setSelectedDateFromPicker(null);
        setActiveId(null); // Clear the drag state
        setShowConfirmation(true);
    };

    const days = [
        { label: "Mon", key: "Monday" },
        { label: "Tue", key: "Tuesday" },
        { label: "Wed", key: "Wednesday" },
        { label: "Thu", key: "Thursday" },
        { label: "Fri", key: "Friday" },
        ...(showSaturday ? [{ label: "Sat", key: "Saturday" }] : []),
    ];

    const getMainStatusColor = (status) => {
        switch (status) {
            case "pending_design": return 'bg-yellow-500';
            case "in_design": return 'bg-blue-500';
            case "design_completed": return 'bg-green-500';
            case "pending_estimation": return 'bg-orange-500';
            case "in_estimation": return 'bg-purple-500';
            case "estimation_completed": return 'bg-emerald-500';
            case "ready_for_delivery": return 'bg-teal-500';
            case "completed": return 'bg-gray-500';
            default: return 'bg-gray-400';
        }
    };

    const getQuoteIcon = (type) => {
        switch (type) {
            case 'roof': return <FiHome className="w-6 h-6" />;
            case 'wall': return <BrickWall className="w-6 h-6" />;
            case 'floor': return <Grid2X2 className="w-6 h-6" />;
            case 'timber': return <GiWoodBeam className="w-6 h-6" />;
            default: return <FiTool className="w-6 h-6" />;
        }
    };

    // Drag and Drop Handlers
    const handleDragStart = (event) => {
        isDraggingRef.current = true;
        setActiveId(event.active.id);

        const assignment = event.active.data.current?.assignment;
        if (assignment) {
            setDraggedItemCache(assignment);
        }
    };

    const normalizeDesignStatus = (status) => {
        if (status === 'not_required') return 'waiting_assignment';
        return status || 'waiting_assignment';
    };

    const handleDragEnd = (event) => {
        isDraggingRef.current = false;
        const { active, over } = event;

        const assignmentData = draggedItemCache || active.data.current?.assignment;
        setDraggedItemCache(null);
        setActiveId(null);

        // If drag date picker is open, don't process the drop
        if (showDragDatePicker) return;

        if (!over) return;

        // ── Date-picker drop zone ──────────────────────────────────────────────
        if (over.id === 'date-picker-drop-zone') {
            if (assignmentData) {
                setDraggedForDatePicker(assignmentData);
                setShowDragDatePicker(true);
            }
            return;
        }

        // ── Playground card ───────────────────────────────────────────────────
        // if (active.data.current?.fromPlayground) {
        //     const quoteId = assignmentData?.quote_info?.id;
        //     const isOverUnscheduled = over.id === 'unscheduled-zone' || unscheduledOrder.includes(over.id);
        //     if (isOverUnscheduled) {
        //         // Unscheduled drop is instant — no confirmation needed.
        //         // Await the DB flag clear then re-fetch so the card appears in the unscheduled list.
        //         if (quoteId) {
        //             handleRemoveFromPlayground(quoteId)
        //                 .then(() => fetchAssignments())
        //                 .catch(err => console.error('Failed to move from playground to unscheduled:', err));
        //         }
        //         return;
        //     }
        //     // Day drop: don't remove from playground yet — wait for user to confirm.
        //     // pendingMove.fromPlaygroundQuoteId is read in handleConfirmMove.
        // }

        // ── Waiting card dragged out ──────────────────────────────────────────
        // if (active.data.current?.fromWaiting) {
        //     const quoteId = assignmentData?.quote_info?.id;
        //     const jobId = assignmentData?.id;
        //     const clearWaiting = () => {
        //         toggleWaitingJob(jobId, quoteType, true, userProfile, quoteId)
        //             .then(() => waitingPanelRef.current?.removeJob(quoteId || jobId))
        //             .catch(err => console.error('Failed to clear waiting job:', err));
        //     };
        //     const isOverUnscheduled = over.id === 'unscheduled-zone' || unscheduledOrder.includes(over.id);
        //     if (isOverUnscheduled) {
        //         clearWaiting();
        //         return;
        //     }
        //     // Drop on a day — clear waiting flag, then fall through to normal scheduling
        //     clearWaiting();
        // }

        // ── Unscheduled section reorder (both cards in unscheduled) ──────────
        const activeInUnscheduled = unscheduledOrder.includes(active.id);
        const overInUnscheduled = unscheduledOrder.includes(over.id);
        if (activeInUnscheduled && overInUnscheduled) {
            const oldIndex = unscheduledOrder.indexOf(active.id);
            const newIndex = unscheduledOrder.indexOf(over.id);
            if (oldIndex === -1 || oldIndex === newIndex) return;

            // Only sort within the same design_status group so the status-based
            // visual ordering (waiting → in_progress → completed) is never scrambled.
            const activeUA = unscheduledAssignments.find(a => (a.quote_info?.id || a.id) === active.id);
            const overUA = unscheduledAssignments.find(a => (a.quote_info?.id || a.id) === over.id);

            const activeStatus = normalizeDesignStatus(activeUA?.quote_info?.design_status);
            const overStatus = normalizeDesignStatus(overUA?.quote_info?.design_status);

            if (activeUA && overUA && activeStatus !== overStatus) return;
            const reordered = arrayMove(unscheduledOrder, oldIndex, newIndex);
            setUnscheduledOrder(reordered);

            // Lock re-sort from subscription echo for 2s
            reorderLockRef.current = true;
            setTimeout(() => { reorderLockRef.current = false; }, 2000);

            // Persist to DB (fire-and-forget)
            // id in reordered = quote_info.id (unique per quote row)
            const updates = reordered.map((id, idx) => {
                const assignment = unscheduledAssignments.find(a => (a.quote_info?.id || a.id) === id);
                return assignment ? {
                    jobId: assignment.id,
                    quoteType: assignment.quote_info?.quote_type || quoteType,
                    sortOrder: idx,
                    scheduleRowId: assignment.schedule_row_id
                } : null;
            }).filter(Boolean);
            batchUpdateSortOrder(updates).catch(err => console.error('Failed to persist unscheduled sort order:', err));

            return;
        }

        // ── Waiting zone (button drop target) ────────────────────────────────
        if (over.id === 'waiting-zone') {
            const quoteId = assignmentData?.quote_info?.id;
            const jobId = assignmentData?.id;
            const alreadyWaiting = assignmentData?.quote_info?.waiting_job || false;
            if (!alreadyWaiting && (quoteId || jobId)) {
                toggleWaitingJob(jobId, quoteType, false, userProfile, quoteId)
                    .then(() => waitingPanelRef.current?.refresh())
                    .catch(err => console.error('Failed to set waiting job:', err));
            }
            return;
        }

        // ── Playground zone ───────────────────────────────────────────────────
        if (over.id === 'playground-zone') {
            const quoteId = assignmentData?.quote_info?.id;
            if (quoteId) {
                const alreadyPinned = playgroundAssignments.some(p => (p.quote_info?.id || p.id) === quoteId);
                if (!alreadyPinned) {
                    // Optimistic: add card to playground and remove from day/unscheduled lists
                    const pinned = {
                        ...assignmentData,
                        quote_info: {
                            ...assignmentData.quote_info,
                            quote_playground: true,
                            delivery_playground: true,
                            expected_finish_date: null,
                            expected_delivery_date: null,
                        }
                    };
                    setPlaygroundAssignments(prev => [...prev, pinned]);
                    setScheduleData(prev => {
                        const next = {};
                        for (const dk in prev) next[dk] = prev[dk].filter(a => (a.quote_info?.id || a.id) !== quoteId);
                        return next;
                    });
                    setUnscheduledAssignments(prev => prev.filter(a => (a.quote_info?.id || a.id) !== quoteId));
                    setQuotePlayground(quoteId, true)
                        .then(() => {
                            const displayId = assignmentData?.unique_id || assignmentData?.schedule_row_id || '';
                            createJobActionLog({
                                job_id: assignmentData?.id,
                                user_id: userProfile?.id,
                                user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                                user_role: userProfile?.role,
                                action: 'set_playground',
                                details: `Pinned #${displayId} to playground`,
                                quote_type: quoteType,
                                action_origin: `schedule-${quoteType}`,
                                company_id: company.id
                            }).catch(() => { });
                        })
                        .catch(err => {
                            console.error('Failed to set quote playground:', err);
                            // Revert playground add on failure
                            setPlaygroundAssignments(prev => prev.filter(p => (p.quote_info?.id || p.id) !== quoteId));
                            // Re-fetch to restore correct state in lists
                            fetchAssignments();
                        });
                }
            }
            return;
        }

        // ── Unscheduled zone or drop onto an unscheduled card ────────────────
        const isOverUnscheduled =
            over.id === 'unscheduled-zone' || unscheduledOrder.includes(over.id);

        if (isOverUnscheduled) {
            const quoteId = assignmentData?.quote_info?.id || assignmentData?.id;

            let removedFromDay = null;
            for (const [day, ids] of Object.entries(cardOrder)) {
                if (ids.includes(active.id)) {
                    removedFromDay = day;
                    break;
                }
            }

            // 🧹 Optimistic UI remove (always)
            setScheduleData(prev => {
                const next = {};
                for (const dk in prev) {
                    next[dk] = prev[dk].filter(a => (a.quote_info?.id || a.id) !== quoteId);
                }
                return next;
            });

            // 🧠 IMPORTANT: pass ALL flags into pendingMove
            setPendingMove({
                assignment: assignmentData,
                removeDate: true, // 💥 THIS is the key
                quoteType: quoteType,
                isPastDate: false,
                removedFromDay,
                removedQuoteId: quoteId,

                // preserve context
                fromPlaygroundQuoteId: active.data.current?.fromPlayground
                    ? (assignmentData?.quote_info?.id || null)
                    : null,

                // fromWaiting: active.data.current?.fromWaiting || false,
            });

            setPastDateWarning(false);
            setShowConfirmation(true);

            return;
        }

        // ── Resolve which day the item was dropped ON ─────────────────────────
        // over.id can be either a day droppable ("day-Monday") or a sibling card id
        let targetDayKey = null;
        if (over.id.startsWith('day-')) {
            const [, dayKey] = over.id.split('-');
            targetDayKey = dayKey;
        } else {
            // over.id is a card id — find which day contains it
            for (const [day, ids] of Object.entries(cardOrder)) {
                if (ids.includes(over.id)) { targetDayKey = day; break; }
            }
        }

        if (!targetDayKey) return;

        // ── Find which day the dragged card came FROM ─────────────────────────
        let sourceDayKey = null;
        for (const [day, ids] of Object.entries(cardOrder)) {
            if (ids.includes(active.id)) { sourceDayKey = day; break; }
        }

        // ── Same-day reorder: update order + persist to DB ───────────────────
        if (sourceDayKey && sourceDayKey === targetDayKey) {
            const dayIds = cardOrder[sourceDayKey] || [];
            const oldIndex = dayIds.indexOf(active.id);
            const newIndex = dayIds.indexOf(over.id);
            // over.id could be the day container itself (no sibling), skip if same position
            if (oldIndex === -1 || oldIndex === newIndex) return;

            // Only sort within the same design_status group so dragging a
            // "waiting_assignment" card across an "in_progress" card is blocked.
            // This keeps the status filter's visual grouping intact.
            const dayAssignmentsForCheck = scheduleData[sourceDayKey] || [];
            const activeDA = dayAssignmentsForCheck.find(a => (a.quote_info?.id || a.id) === active.id);
            const overDA = dayAssignmentsForCheck.find(a => (a.quote_info?.id || a.id) === over.id);
            if (activeDA && overDA &&
                activeDA.quote_info?.design_status !== overDA.quote_info?.design_status) return;
            const reordered = arrayMove(dayIds, oldIndex, newIndex);
            setCardOrder(prev => ({ ...prev, [sourceDayKey]: reordered }));

            // Lock re-sort from subscription echo for 2s so our local order isn't disrupted
            reorderLockRef.current = true;
            setTimeout(() => { reorderLockRef.current = false; }, 2000);

            // Persist new positions to DB (fire-and-forget)
            // id in reordered = quote_info.id (unique per quote row)
            const dayAssignments = scheduleData[sourceDayKey] || [];
            const updates = reordered.map((id, idx) => {
                const assignment = dayAssignments.find(a => (a.quote_info?.id || a.id) === id);
                return assignment ? {
                    jobId: assignment.id,
                    quoteType: assignment.quote_info?.quote_type || quoteType,
                    sortOrder: idx,
                    scheduleRowId: assignment.schedule_row_id
                } : null;
            }).filter(Boolean);
            batchUpdateSortOrder(updates).catch(err => console.error('Failed to persist sort order:', err));

            return;
        }

        // ── Cross-day move: show confirmation popup ───────────────────────────
        const dayIndex = days.findIndex(d => d.key === targetDayKey);
        if (dayIndex === -1) return;

        const targetDate = getDayDateObject(dayIndex);
        const newDate = new Date(targetDate);
        newDate.setUTCHours(10, 0, 0, 0);
        const isPast = isDateInPast(newDate);

        setPendingMove({
            assignment: assignmentData,
            newDate: newDate,
            dayKey: targetDayKey,
            quoteType: quoteType,
            isPastDate: isPast,
            // If dragged from playground, record quoteId so confirm can clear it
            fromPlaygroundQuoteId: active.data.current?.fromPlayground
                ? (assignmentData?.quote_info?.id || null)
                : null,
        });
        setPastDateWarning(isPast);
        setShowConfirmation(true);
    };

    const handleDragOver = (event) => {
        const { over } = event;
        // Could add visual feedback here if needed
    };

    // Mobile handlers
    const handleMobileMove = useCallback((assignment, e, fromPlayground = false) => {
        e.stopPropagation();
        setSelectedAssignment({ ...assignment, mobileFromPlayground: fromPlayground });
        setShowMoveModal(true);
    }, []);

    const handleMobileMoveConfirm = (newDate) => {
        if (!selectedAssignment || !['admin', 'super_admin'].includes(userProfile?.role)) return;
        const isPast = newDate ? isDateInPast(newDate) : false;
        setPendingMove({ assignment: selectedAssignment, newDate,
            removeDate: !newDate,
            dayKey: newDate?.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' }),
            isPastDate: isPast, quoteType, fromPlaygroundQuoteId: selectedAssignment.mobileFromPlayground ? selectedAssignment.quote_info?.id : null, });
        setPastDateWarning(isPast);
        setShowMoveModal(false);
        setShowConfirmation(true);
    };

    const handleConfirmMove = async () => {
        if (!pendingMove) return;

        try {
            setIsLoading(true);

            const quoteId = pendingMove.assignment.quote_info?.id || null;

            // Mark this quote ID as locally-moved so our own subscription echo is skipped
            const selfKey = quoteId || `${pendingMove.assignment.id}:${quoteType}`;
            selfMovedRef.current.add(selfKey);
            setTimeout(() => selfMovedRef.current.delete(selfKey), 5000);

            const { error } = await updateFinishDate(
                pendingMove.assignment.id,
                quoteType,
                pendingMove.removeDate ? null : pendingMove.newDate,
                quoteId
            );

            if (error) throw error;

            // ── Activity log ────────────────────────────────────────────────
            {
                const displayId = pendingMove.assignment?.schedule_row_id || pendingMove.assignment?.unique_id || '';
                const details = pendingMove.removeDate
                    ? `Removed #${displayId} from schedule`
                    : `Scheduled #${displayId} to ${pendingMove.dayKey} (${format(pendingMove.newDate, 'd MMM')})`;
                createJobActionLog({
                    job_id: pendingMove.assignment.id,
                    user_id: userProfile?.id,
                    user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                    user_role: userProfile?.role,
                    action: pendingMove.removeDate ? 'unschedule' : 'reschedule',
                    details,
                    quote_type: quoteType,
                    action_origin: `schedule-${quoteType}`,
                    company_id: company.id
                }).catch(() => { });

                createJobActionLog({
                    job_id: pendingMove.assignment.id,
                    user_id: userProfile?.id,
                    user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                    user_role: userProfile?.role,
                    action: pendingMove.removeDate ? 'unschedule' : 'reschedule',
                    details,
                    quote_type: quoteType,
                    action_origin: `schedule-delivery`,
                    company_id: company.id
                }).catch(() => { });
            }

            // Reset sort_order so this card appends at the bottom of the new day
            // (fire-and-forget — non-critical)
            resetSortOrder(pendingMove.assignment.id, quoteType, quoteId)
                .catch(err => console.error('Failed to reset sort order:', err));

            // If this was a playground card, await DB flag clear before fetching
            // so fetchAssignments doesn't get the card filtered out by quote_playground=true
            if (pendingMove.fromPlaygroundQuoteId) {
                await handleRemoveFromPlayground(pendingMove.fromPlaygroundQuoteId);
            }

            // if (pendingMove.fromWaiting) {
            //     await toggleWaitingJob(
            //         pendingMove.assignment.id,
            //         quoteType,
            //         true,
            //         userProfile,
            //         quoteId
            //     );
            // }

            await fetchAssignments();
        } catch (error) {
            console.error("Failed to update assignment date:", error);
        } finally {
            setIsLoading(false);
            setShowConfirmation(false);
            setPendingMove(null);
            setActiveId(null);
            setShowOrderWarningDetails(false);
        }
    };

    const handleCancelMove = () => {
        // If we optimistically removed a card from a day, restore it on cancel
        if (pendingMove?.removedFromDay && pendingMove?.assignment) {
            const day = pendingMove.removedFromDay;
            const assignment = pendingMove.assignment;
            setScheduleData(prev => {
                if (!prev[day]) return prev;
                // Only restore if not already present (prevent duplicates)
                const alreadyThere = prev[day].some(a => (a.quote_info?.id || a.id) === pendingMove.removedQuoteId);
                if (alreadyThere) return prev;
                return { ...prev, [day]: [...prev[day], assignment] };
            });
        }
        setShowConfirmation(false);
        setPendingMove(null);
        setActiveId(null);
        setPastDateWarning(false);
        setShowOrderWarningDetails(false);
    };

    const handleOpenAssignment = useCallback((assignmentId) => {
        window.open(`/${company?.slug}/assignment/${assignmentId}`, '_blank');
    }, [company?.slug]);

    const handleEditAssignment = useCallback((assignmentId) => {
        setEditJobId(assignmentId);
        setEditOpen(true);
    }, []);

    const handleToggleQuoteHold = useCallback(async (assignmentId, quoteType, currentStatus) => {
        try {
            const result = await toggleQuoteHold(assignmentId, quoteType, currentStatus, userProfile);

            if (result?.success) {
                Toast.success(result.message || 'Quote updated successfully');
            } else {
                Toast.error(result?.error || 'Failed to pause/resume quote.');
            }


        } catch (error) {
            console.error('Error in handleToggleQuoteHold:', error);
            Toast.error('Failed to pause/resume quote');
        }
    }, [userProfile]);

    const handleToggleLayoutOnly = useCallback(async (assignmentId, quoteType, currentStatus, quoteId, displayId = null) => {
        try {
            await toggleLayoutOnly(assignmentId, quoteType, currentStatus, userProfile, quoteId);
            const label = displayId || assignmentId;
            const details = currentStatus ? `Removed Layout Only from #${label}` : `Marked #${label} as Layout Only`;
            createJobActionLog({
                job_id: assignmentId,
                user_id: userProfile?.id,
                user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                user_role: userProfile?.role,
                action: 'toggle_layout_only',
                details,
                quote_type: quoteType,
                action_origin: `schedule-${quoteType}`,
                company_id: company.id
            }).catch(() => { });
            Toast.success(currentStatus ? 'Layout Only removed' : 'Marked as Layout Only');
        } catch (error) {
            console.error('Error in handleToggleLayoutOnly:', error);
            Toast.error('Failed to update Layout Only');
        }
    }, [userProfile, quoteType]);

    const handleRemoveFromPlayground = async (quoteId) => {
        // Optimistic: remove from playground state immediately
        const removed = playgroundAssignments.find(p => (p.quote_info?.id || p.id) === quoteId);
        setPlaygroundAssignments(prev => prev.filter(p => (p.quote_info?.id || p.id) !== quoteId));
        try {
            await setQuotePlayground(quoteId, false);
        } catch (err) {
            console.error('Failed to remove from playground:', err);
            // Revert back on failure
            if (removed) setPlaygroundAssignments(prev => [...prev, removed]);
            throw err; // re-throw so callers can react
        }
    };

    const handleToggleWaiting = useCallback(async (assignmentId, quoteType, currentStatus, quoteId = null, displayId = null) => {
        try {
            await toggleWaitingJob(assignmentId, quoteType, currentStatus, userProfile, quoteId);
            const label = displayId || assignmentId;
            const details = currentStatus ? `Removed Waiting from #${label}` : `Marked #${label} as Waiting Only`;
            createJobActionLog({
                job_id: assignmentId,
                user_id: userProfile?.id,
                user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                user_role: userProfile?.role,
                action: 'toggle_waiting',
                details,
                quote_type: quoteType,
                action_origin: `schedule-${quoteType}`,
                company_id: company?.id
            }).catch(() => { });
            Toast.success(currentStatus ? 'Waiting removed' : 'Marked as Waiting');
        } catch (error) {
            console.error('Error in handleToggleWaiting:', error);
            Toast.error('Failed to update Waiting status');
        }
    }, [userProfile, quoteType]);

    const handleToggleTimberChecked = useCallback(async (assignment) => {
        const quoteId = assignment?.quote_info?.id;
        if (!quoteId) return;
        const current = !!assignment?.quote_info?.timber_is_checked;
        const next = !current;

        // Optimistic update — patch only the matching quote row by UUID
        const patchQuoteInfo = (a) =>
            a.quote_info?.id === quoteId
                ? { ...a, quote_info: { ...a.quote_info, timber_is_checked: next } }
                : a;

        setScheduleData(prev => {
            const updated = { ...prev };
            for (const day of Object.keys(updated)) {
                updated[day] = updated[day].map(patchQuoteInfo);
            }
            return updated;
        });
        setUnscheduledAssignments(prev => prev.map(patchQuoteInfo));

        try {
            await toggleTimberIsChecked(quoteId, next);
        } catch (error) {
            console.error('Error toggling timber_is_checked:', error);
            Toast.error('Failed to update timber check');
            // Revert optimistic update
            const revert = (a) =>
                a.quote_info?.id === quoteId
                    ? { ...a, quote_info: { ...a.quote_info, timber_is_checked: current } }
                    : a;
            setScheduleData(prev => {
                const reverted = { ...prev };
                for (const day of Object.keys(reverted)) {
                    reverted[day] = reverted[day].map(revert);
                }
                return reverted;
            });
            setUnscheduledAssignments(prev => prev.map(revert));
        }
    }, [userProfile, quoteType]);

    const handleToggleSentProduction = useCallback(async (assignmentId, quoteType, currentStatus, quoteId = null) => {
        try {
            await toggleSentProduction(assignmentId, quoteType, currentStatus, userProfile, quoteId);
            Toast.success(currentStatus ? 'Removed from Production' : 'Sent to Production');
        } catch (error) {
            console.error('Error toggling sent_production:', error);
            Toast.error('Failed to update Production status');
        }
    }, [userProfile, quoteType]);


    const DEPARTMENT_ORDER = {
        wall: 0,
        floor: 1,
        roof: 2,
    };

    const getQuoteDate = (quote) => {
        return (
            quote?.expected_delivery_date ||
            null
        );
    };

    const toDayTime = (value) => {
        if (!value) return null;

        const date = value instanceof Date ? value : new Date(value);

        if (Number.isNaN(date.getTime())) return null;

        return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
        ).getTime();
    };

    const formatWarningDate = (date) => {
        if (!date) return 'No date';
        return new Date(date).toLocaleDateString('en-AU', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const getScheduleOrderWarnings = (move) => {
        if (!move?.assignment || !move?.newDate) return [];

        const movingQuote = move.assignment.quote_info || {};

        const movingType =
            movingQuote.quote_type ||
            move.quoteType ||
            quoteType;

        const movingQuoteId =
            movingQuote.id ||
            movingQuote.quoteId ||
            null;

        const movingOrder = DEPARTMENT_ORDER[movingType];

        if (movingOrder === undefined) return [];

        const newTime = toDayTime(move.newDate);

        if (!newTime) return [];

        const allQuotes = [
            ...(move.assignment.job_quotes || []),
        ];

        // Make sure current quote exists in the list shape too
        if (movingQuote?.id && !allQuotes.some(q => q.id === movingQuote.id)) {
            allQuotes.push(movingQuote);
        }

        return allQuotes
            .filter(q => {
                if (!q) return false;


                const otherType = q.quote_type;
                const otherOrder = DEPARTMENT_ORDER[otherType];

                if (otherOrder === undefined) return false;

                const otherQuoteId = q.id || q.quoteId || null;

                if (movingQuoteId && otherQuoteId === movingQuoteId) return false;

                // Ignore old duplicate delivery-count rows if needed
                if (q.delivery_count && q.delivery_count !== 0) return false;

                const otherDate = getQuoteDate(q);
                const otherTime = toDayTime(otherDate);

                if (!otherTime) return false;

                // Wall must be before Floor/Roof
                // Floor must be after Wall and before Roof
                // Roof must be after Wall/Floor
                const earlierDepartmentIsAfterNewDate =
                    otherOrder < movingOrder && otherTime > newTime;

                const laterDepartmentIsBeforeNewDate =
                    otherOrder > movingOrder && otherTime < newTime;

                return earlierDepartmentIsAfterNewDate || laterDepartmentIsBeforeNewDate;
            })
            .map(q => {
                const otherOrder = DEPARTMENT_ORDER[q.quote_type];
                const otherDate = getQuoteDate(q);

                return {
                    id: q.id || q.quoteId || `${q.quote_type}-${q.child_unique_id}`,
                    quote_type: q.quote_type,
                    child_unique_id: q.child_unique_id || q.quote_type,
                    date: otherDate,
                    message:
                        otherOrder < movingOrder
                            ? `${q.child_unique_id || q.quote_type} is a ${q.quote_type} job currently set after this new ${movingType} date.`
                            : `${q.child_unique_id || q.quote_type} is a ${q.quote_type} job currently set before this new ${movingType} date.`,
                };
            })
            .sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    const orderWarnings = getScheduleOrderWarnings(pendingMove);
    const visibleOrderWarnings = showOrderWarningDetails
        ? orderWarnings
        : orderWarnings.slice(0, 3);
    const hiddenOrderWarningCount = Math.max(orderWarnings.length - visibleOrderWarnings.length, 0);

    const renderAssignmentCard = useCallback((assignment, playgroundMode = false) => {
        const notesOpen = openNotesBubble?.quoteId === assignment?.quote_info?.id;
        const notesBubblePos = notesOpen ? openNotesBubble : DEFAULT_NOTES_POS;
        const cardKey = assignment?.quote_info?.id || assignment?.id;

        return (
            <SortableCard
                key={cardKey}
                assignment={assignment}
                playgroundMode={playgroundMode}
                isDisabled={userProfile?.role !== 'admin' && userProfile?.role !== 'super_admin'}
                quoteType={quoteType}
                userRole={userProfile?.role}
                isMobile={isMobile}
                companySlug={companySlug}
                navigate={navigate}
                notesOpen={notesOpen}
                notesBubblePos={notesBubblePos}
                onOpen={handleOpenAssignment}
                onEdit={handleEditAssignment}
                onDuplicateJob={onDuplicateJob}
                onToggleQuoteHold={handleToggleQuoteHold}
                onToggleLayoutOnly={handleToggleLayoutOnly}
                onToggleWaiting={handleToggleWaiting}
                onToggleSentProduction={handleToggleSentProduction}
                onMobileMove={handleMobileMove}
                onToggleTimberChecked={handleToggleTimberChecked}
                setOpenNotesBubble={setOpenNotesBubble}
                setScheduleData={setScheduleData}
                setUnscheduledAssignments={setUnscheduledAssignments}
                setPlaygroundAssignments={setPlaygroundAssignments}
                userProfile={userProfile}
            />
        );
    }, [
        openNotesBubble,
        userProfile,
        quoteType,
        isMobile,
        companySlug,
        navigate,
        handleOpenAssignment,
        handleEditAssignment,
        onDuplicateJob,
        handleToggleQuoteHold,
        handleToggleLayoutOnly,
        handleToggleWaiting,
        handleToggleSentProduction,
        handleMobileMove,
        handleToggleTimberChecked,
    ]);

    // DroppableDay, DroppableUnscheduled, DroppablePlayground, DroppableDatePickerZone
    // are now defined at module level (above the component) to keep stable function
    // references across renders and prevent scroll position resets.


    // Mobile List View
    const MobileListView = () => {
        // Get the current week key based on offset (we use a fixed key for fetching)
        const currentWeekKey = `week${weekOffset}`;

        return (
            <div className="space-y-3">
                {days.map(({ label, key }, dayIndex) => {
                    const dayAssignments = scheduleData[key] || [];
                    const dayIsToday = isDayToday(dayIndex);
                    const dayIsPast = isDateInPast(getDayDateObject(dayIndex));

                    return (
                        <div key={key} className={`bg-white rounded-lg border ${dayIsPast ? 'border-gray-300 bg-gray-50/50' : 'border-gray-200'} shadow-sm`}>
                            <div
                                className={`px-4 py-3 ${dayIsPast ? 'bg-gray-100' : 'bg-gray-50'} rounded-t-lg cursor-pointer hover:bg-gray-100 transition-colors`}
                                onClick={() => setSelectedDay(selectedDay === key ? null : key)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 ${dayIsPast ? 'bg-gray-200' : 'bg-blue-100'} rounded-lg flex items-center justify-center`}>
                                            <span className={`font-bold ${dayIsPast ? 'text-gray-600' : 'text-blue-600'} text-sm`}>{label}</span>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900">{`${label}, ${getDayDate(dayIndex)}`}</h3>
                                            <p className="text-sm text-gray-500">{dayAssignments.length} assignment{dayAssignments.length !== 1 ? 's' : ''}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {dayIsToday && (
                                            <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                                                Today
                                            </span>
                                        )}
                                        {dayIsPast && !dayIsToday && (
                                            <span className="bg-gray-200 text-gray-600 text-xs font-medium px-2 py-1 rounded-full">
                                                Past
                                            </span>
                                        )}
                                        <FiChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${selectedDay === key ? 'rotate-90' : ''
                                            }`} />
                                    </div>
                                </div>
                            </div>

                            {selectedDay === key && (
                                <div className="p-4 border-t border-gray-100 space-y-3">
                                    {dayAssignments.length > 0 ? dayAssignments.map((assignment) => (
                                        renderAssignmentCard(assignment)
                                    )) : (
                                        <div className="text-center py-6">
                                            <FiCalendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                                            <p className="text-gray-500">No assignments scheduled</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // activeId = quote_info.id (unique per quote row), so match by that first, then fallback
    const draggedAssignment = draggedItemCache || (activeId
        ? [...Object.values(scheduleData || {}).flat(), ...unscheduledAssignments]
            .find(a => (a.quote_info?.id || a.id) === activeId)
        : null);

    return (
        <div className="quote-schedule-page contents space-y-8">
            <DndContext
                sensors={isMobile ? [] : sensors}
                collisionDetection={quoteScheduleCollision}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                measuring={{
                    droppable: {
                        strategy: MeasuringStrategy.BeforeDragging,
                    },
                }}
            >
                <>
                    {/* Header */}
                    <div className="quote-schedule-header flex flex-row w-full justify-between">
                        <div className="flex flex-col items-start justify-start gap-4">

                            <BackButton
                                text="Back to Schedule Hub"
                                variant="ghost"
                                size="sm"
                                iconType="chevron"
                                className="text-blue-600 hover:text-blue-800"
                            />

                            <div className="self-start">
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                                    {getQuoteIcon(quoteType)}
                                    {quoteType?.charAt(0).toUpperCase() + quoteType?.slice(1)} Schedule
                                </h1>
                                <p className="text-gray-600 mt-1">Manage and track construction job timelines</p>
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowMonthlyInvoices((prev) => !prev)}
                                className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${showMonthlyInvoices
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                                    }`}
                            >
                                {showMonthlyInvoices ? "View Weekly Schedule" : "View Monthly Invoices"}
                            </button>
                        </div>
                        {/* ── Schedule activity log (fixed top-right panel) ── */}
                        <ScheduleActivityLog actionOrigin={`schedule-${quoteType}`} />
                    </div>


                    {showMonthlyInvoices ? (
                        <MonthlyInvoiceView company={company} quoteType={quoteType} cancelledFilter={cancelledFilter} />
                    ) : (
                        <>
                            {/* your current DndContext / weekly schedule content stays here */}




                            {/* Week Navigation */}
                            <div className={`quote-schedule-toolbar sticky top-0 z-[100] bg-white shadow-sm border border-gray-200 p-4 ${isScrolled ? "rounded-none" : "rounded-xl"}`}>
                                <div className="flex items-center justify-between gap-4">
                                    {/* Previous Month */}
                                    <button
                                        onClick={() => setWeekOffset(weekOffset - 4)}
                                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all font-medium group"
                                        title="Go back 4 weeks"
                                    >
                                        <FiChevronsLeft className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                        <span className="hidden sm:inline">Month</span>
                                    </button>

                                    {/* Previous Week */}
                                    <button
                                        onClick={() => setWeekOffset(weekOffset - 1)}
                                        className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all group"
                                        title="Previous week"
                                    >
                                        <FiChevronLeft className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                        <span className="hidden md:inline text-sm">Week</span>
                                    </button>

                                    {/* Current Week Display - Droppable Zone */}
                                    <DroppableDatePickerZone>
                                        <button
                                            onClick={handleOpenDatePicker}
                                            className="w-full hover:bg-gray-50 rounded-lg p-2 transition-colors group"
                                            title="Pick a specific week or drop assignment here"
                                        >
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center justify-center gap-2">
                                                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                                                        {getMonthLabel()}
                                                    </h2>
                                                    {weekOffset === 0 && (
                                                        <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                                                            This Week
                                                        </span>
                                                    )}
                                                    {weekOffset < 0 && (
                                                        <span className="bg-gray-200 text-gray-700 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                                                            {Math.abs(weekOffset)} {Math.abs(weekOffset) === 1 ? 'week' : 'weeks'} ago
                                                        </span>
                                                    )}
                                                    {weekOffset > 0 && (
                                                        <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                                                            In {weekOffset} {weekOffset === 1 ? 'week' : 'weeks'}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-600 font-medium group-hover:text-blue-600 transition-colors">
                                                    {getWeekLabel()}
                                                </p>
                                                <p className="text-xs text-gray-400 group-hover:text-blue-500 transition-colors">
                                                    {activeId ? 'Drop here to select week' : 'Click to select date'}
                                                </p>
                                            </div>
                                        </button>
                                    </DroppableDatePickerZone>

                                    {/* Next Week */}
                                    <button
                                        onClick={() => setWeekOffset(weekOffset + 1)}
                                        className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all group"
                                        title="Next week"
                                    >
                                        <span className="hidden md:inline text-sm">Week</span>
                                        <FiChevronRight className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                    </button>

                                    {/* Next Month */}
                                    <button
                                        onClick={() => setWeekOffset(weekOffset + 4)}
                                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all font-medium group"
                                        title="Go forward 4 weeks"
                                    >
                                        <span className="hidden sm:inline">Month</span>
                                        <FiChevronsRight className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                    </button>
                                </div>

                                {/* Quick Navigation to Today */}




                                <div className="mt-3 pt-3 border-t flex flex-row flex-wrap justify-between items-center gap-4 transition-all duration-200">
                                    <div className="flex flex-row flex-wrap gap-4 items-center self-center">
                                        {/* <div className={`flex flex-row items-center gap-1 text-xl font-bold text-nowrap overflow-hidden ${isScrolled ? "max-w-[300px]" : "max-w-0"} transition-all ease-in-out duration-500`}>
                                            {getQuoteIcon(quoteType)}
                                            {quoteType?.charAt(0).toUpperCase() + quoteType?.slice(1)} Schedule
                                        </div> */}

                                        {/* Search Bar */}
                                        <div className="flex items-center gap-2">
                                            <div className="relative flex-1 max-w-md">
                                                <input
                                                    type="text"
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    placeholder="Search by Job ID, Customer, Address, Designer..."
                                                    className="lg:w-96 w-full px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                                                />
                                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                {searchQuery && (
                                                    <button
                                                        onClick={() => setSearchQuery('')}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                                    >
                                                        <FiX className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>


                                        <div className="flex items-center gap-2">
                                            <select
                                                value={selectedUser}
                                                onChange={(e) => setSelectedUser(e.target.value)}
                                                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-blue-500 focus:border-blue-500 min-w-[160px] bg-white"
                                            >
                                                <option value="">All Users</option>
                                                {availableUsers.designers?.length > 0 && (
                                                    <optgroup label="Designers">
                                                        {availableUsers.designers.map(user => (
                                                            <option key={user.userId} value={user.userId}>
                                                                {user.username}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                )}
                                                {availableUsers.estimators?.length > 0 && (
                                                    <optgroup label="Estimators">
                                                        {availableUsers.estimators.map(user => (
                                                            <option key={user.userId} value={user.userId}>
                                                                {user.username}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                )}
                                            </select>
                                        </div>

                                        {/* Design Status Filter */}
                                        <div className="flex items-center gap-2">
                                            <select
                                                value={designStatusFilter}
                                                onChange={(e) => setDesignStatusFilter(e.target.value)}
                                                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-blue-500 focus:border-blue-500 min-w-[160px] bg-white"
                                            >
                                                <option value="all">All Jobs</option>
                                                <option value="waiting_assignment">Waiting Assignment</option>
                                                <option value="in_progress">In Design</option>
                                                <option value="completed">Completed</option>
                                                <option value="on_hold">On Hold</option>
                                            </select>
                                        </div>

                                        <div className="relative">
                                            <select
                                                value={cancelledFilter}
                                                onChange={(e) => setCancelledFilter(e.target.value)}
                                                className="px-3 py-1 rounded-md border text-sm font-medium bg-white text-gray-700 border-gray-300"
                                            >
                                                <option value="active_only">Active Only</option>
                                                <option value="active_cancelled">Active + Cancelled</option>
                                                <option value="cancelled_only">Cancelled Only</option>
                                            </select>
                                        </div>

                                        {/* Saturday Toggle */}
                                        <button
                                            onClick={() => setShowSaturday(prev => !prev)}
                                            className={`flex items-center gap-1.5 px-3 py-1 rounded-md border text-sm font-medium transition-colors ${showSaturday
                                                ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                                : 'border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100'
                                                }`}
                                        >
                                            {showSaturday ? 'Hide Saturday' : 'Saturday Hidden'}
                                        </button>
                                    </div>
                                    <div className="flex flex-row items-center gap-2">
                                        {weekOffset !== 0 && (
                                            <button
                                                onClick={() => setWeekOffset(0)}
                                                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-medium text-sm shadow-sm hover:shadow flex items-center gap-2 mx-auto"
                                            >
                                                <FiCalendar className="w-4 h-4" />
                                                Jump to This Week
                                            </button>
                                        )}
                                        {/* Waiting Jobs Panel — inside DndContext so cards are draggable */}
                                        {/* <WaitingJobsPanel
                                    ref={waitingPanelRef}
                                    quoteType={quoteType}
                                    companyId={company?.id}
                                    userId={userProfile?.id}
                                    showOnlyMine={showOnlyMine}
                                /> */}
                                        {/* On-Hold Panel — lives inside DndContext so cards are draggable */}
                                        <OnHoldPanel
                                onMobileMove={["admin", "super_admin"].includes(userProfile?.role) ? handleMobileMove : undefined}
                                            page="department"
                                            quoteType={quoteType}
                                            companyId={company?.id}
                                            userId={userProfile?.id}
                                            showOnlyMine={showOnlyMine}
                                        />
                                    </div>
                                </div>
                            </div>

                            <OutOfWeekJobSearch
                                companyId={company?.id}
                                quoteType={quoteType}
                                currentWeekStart={getCurrentWeekStart()}
                                searchQuery={debouncedSearchQuery}
                                showOnlyMine={showOnlyMine}
                                currentUserId={userProfile?.id}
                                selectedUser={selectedUser}
                                designStatusFilter={designStatusFilter}
                                cancelledFilter={cancelledFilter}
                                renderAssignmentCard={renderAssignmentCard}
                                onJumpToWeek={jumpToWeek}
                            />

                            {/* Content - Grid or List View */}
                            {isMobile && mobileViewMode === 'list' ? (
                                <MobileListView />
                            ) : isLoading ? (
                                <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${showSaturday ? "xl:grid-cols-6" : "xl:grid-cols-5"}`}>
                                    {days.map(({ label, key }, dayIndex) => (
                                        <DayColumnSkeleton key={key} dayLabel={label} dayDate={getDayDate(dayIndex)} />
                                    ))}
                                </div>
                            ) : (
                                <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${showSaturday ? "xl:grid-cols-6" : "xl:grid-cols-5"}`}>
                                    {days.map(({ label, key }, dayIndex) => {
                                        const allDayAssignments = scheduleData[key] || [];
                                        // Apply manual sort order, then search filter
                                        // rowId = quote_info.id (unique per quote row) used for DnD + React keys
                                        const rowId = (a) => a.quote_info?.id || a.id;
                                        const order = cardOrder[key] || [];
                                        const manualSorted = [
                                            ...order
                                                .map(id => allDayAssignments.find(a => rowId(a) === id))
                                                .filter(Boolean),
                                            // Append any assignments not yet in order (shouldn't happen but safe)
                                            ...allDayAssignments.filter(a => !order.includes(rowId(a))),
                                        ];

                                        // Sort by design_status: waiting_assignment → in_progress → completed
                                        // const DESIGN_ORDER = { waiting_assignment: 0, in_progress: 1, completed: 2 };
                                        // const sortedAssignments = [...manualSorted].sort((a, b) => {
                                        //     const aStatus = normalizeDesignStatus(a.quote_info?.design_status);
                                        //     const bStatus = normalizeDesignStatus(b.quote_info?.design_status);

                                        //     const aS = DESIGN_ORDER[aStatus] ?? 999;
                                        //     const bS = DESIGN_ORDER[bStatus] ?? 999;

                                        //     return aS - bS;
                                        // });

                                        // Sort by delivery_time_slot first, then design_status inside each slot
                                        const DELIVERY_SLOT_ORDER = {
                                            'First Delivery': 0,
                                            'Second Delivery': 1,
                                            'Before Lunch': 2,
                                            'Lunchtime': 3,
                                            'After Lunch': 4,
                                            'Last Delivery': 5,
                                        };

                                        const DESIGN_ORDER = {
                                            waiting_assignment: 0,
                                            not_required: 0,
                                            waiting_design: 1,
                                            in_progress: 1,
                                            completed: 2,
                                        };

                                        const sortedAssignments = [...manualSorted].sort((a, b) => {
                                            const aHasSlot = !!a?.quote_info?.delivery_time_slot;
                                            const bHasSlot = !!b?.quote_info?.delivery_time_slot;

                                            const aSlot = DELIVERY_SLOT_ORDER[a?.quote_info?.delivery_time_slot] ?? 999;
                                            const bSlot = DELIVERY_SLOT_ORDER[b?.quote_info?.delivery_time_slot] ?? 999;

                                            // 1. Jobs with delivery slot come first
                                            if (aHasSlot !== bHasSlot) {
                                                return aHasSlot ? -1 : 1;
                                            }

                                            // 2. If both have slots, sort by delivery slot order
                                            if (aHasSlot && bHasSlot && aSlot !== bSlot) {
                                                return aSlot - bSlot;
                                            }

                                            const aStatus = normalizeDesignStatus(a?.quote_info?.design_status);
                                            const bStatus = normalizeDesignStatus(b?.quote_info?.design_status);

                                            const aDesign = DESIGN_ORDER[aStatus] ?? 999;
                                            const bDesign = DESIGN_ORDER[bStatus] ?? 999;

                                            // 3. Inside same slot OR inside no-slot group, sort by design status
                                            if (aDesign !== bDesign) {
                                                return aDesign - bDesign;
                                            }

                                            return 0;
                                        });

                                        const dayAssignments = filterAssignments(sortedAssignments);
                                        const sortedIds = sortedAssignments.map(rowId);
                                        const completedCount = dayAssignments.filter(a => {
                                            const status = getJobStatusSummary(a, "design")?.toLowerCase();
                                            return status === "completed";
                                        }).length;

                                        const notAssignedCount = dayAssignments.filter(a => {
                                            const status = a?.quote_info?.design_status || a?.design_status;

                                            return (
                                                status === "not_required" ||
                                                status === "waiting_assignment"
                                            );
                                        }).length;

                                        const inDesignCount = dayAssignments.filter(a => {
                                            const status = a?.quote_info?.design_status || a?.design_status;

                                            return (
                                                status === "in_progress" ||
                                                status === "in_design" ||
                                                status === "waiting_design"
                                            );
                                        }).length;

                                        const dayObj = getDayDateObject(dayIndex);

                                        return (
                                            <VirtualDroppableDay
                                                key={key}
                                                dayKey={key}
                                                dayLabel={label}
                                                dayDate={getDayDate(dayIndex)}
                                                dayIsPast={isDateInPast(dayObj)}
                                                dayIsToday={isDayToday(dayIndex)}
                                                dayHolidays={getHolidaysForDate(dayObj)}
                                                assignments={dayAssignments}
                                                sortedIds={sortedIds}
                                                jobCount={dayAssignments.length}
                                                completedCount={completedCount}
                                                notAssignedCount={notAssignedCount}
                                                inDesignCount={inDesignCount}
                                                isFiltered={!!debouncedSearchQuery.trim()}
                                                renderCard={(assignment) => (
                                                    renderAssignmentCard(assignment)
                                                )}
                                                showSaturday={showSaturday}
                                            />
                                        );
                                    })}
                                </div>
                            )}

                            {/* Unscheduled Assignments + Playground Section */}
                            <div className="flex flex-col lg:flex-row gap-4 items-stretch">
                                {/* Left: Unscheduled Assignments */}
                                <div className="w-full lg:w-1/2">
                                    {isLoading ? (
                                        <UnscheduledSectionSkeleton />
                                    ) : (
                                        <DroppableUnscheduled>
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-lg font-semibold text-gray-900">Unscheduled Assignments</h3>
                                                <div className="flex items-center gap-2">
                                                    <span className="bg-orange-100 text-orange-800 text-sm font-medium px-3 py-1 rounded-full">
                                                        {filterAssignments(unscheduledAssignments)?.length || 0} assignments
                                                        {debouncedSearchQuery.trim() && (
                                                            <span className="text-orange-600 ml-1">(filtered)</span>
                                                        )}
                                                    </span>
                                                    {!isMobile && (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                                                        <span className="text-xs text-gray-500">Drop here to unschedule</span>
                                                    )}
                                                </div>
                                            </div>

                                            {(() => {
                                                const rowId = (a) => a.quote_info?.id || a.id;
                                                const sortedUnscheduled = [
                                                    ...unscheduledOrder
                                                        .map(id => unscheduledAssignments.find(a => rowId(a) === id))
                                                        .filter(Boolean),
                                                    ...unscheduledAssignments.filter(a => !unscheduledOrder.includes(rowId(a))),
                                                ];
                                                const filteredUnscheduled = filterAssignments(sortedUnscheduled);
                                                const sortedUnscheduledIds = sortedUnscheduled.map(rowId);
                                                return (
                                                    <VirtualUnscheduledGrid
                                                        assignments={filteredUnscheduled}
                                                        sortedIds={sortedUnscheduledIds}
                                                        isFiltered={!!debouncedSearchQuery.trim()}
                                                        renderCard={(assignment) => (
                                                            renderAssignmentCard(assignment)
                                                        )}
                                                    />
                                                );
                                            })()}
                                        </DroppableUnscheduled>
                                    )}
                                </div>

                                {/* Right: Playground */}
                                <div className="w-full lg:w-1/2">
                                    <DroppablePlayground>
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                <span className="capitalize">{quoteType}</span> Playground
                                            </h3>
                                            <span className="bg-purple-100 text-purple-800 text-sm font-medium px-3 py-1 rounded-full">
                                                {filterAssignments(playgroundAssignments).length} pinned
                                                {debouncedSearchQuery.trim() && (
                                                    <span className="text-purple-600 ml-1">(filtered)</span>
                                                )}
                                            </span>
                                        </div>
                                        <VirtualPlaygroundGrid
                                            assignments={filterAssignments(playgroundAssignments)}
                                            renderCard={(assignment) => {
                                                const pgRowId = assignment.quote_info?.id || assignment.id;
                                                return (
                                                    <div key={pgRowId} className="relative group">
                                                        <button
                                                            onClick={() => handleRemoveFromPlayground(pgRowId)}
                                                            className="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-0.5 shadow border border-gray-200 hover:bg-red-50 hover:border-red-300"
                                                            title="Remove from playground"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                            </svg>
                                                        </button>
                                                        {renderAssignmentCard(assignment, true)}
                                                    </div>
                                                );
                                            }}
                                        />
                                    </DroppablePlayground>
                                </div>
                            </div>

                            {/* Status Legend */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="font-medium text-gray-900 mb-3">Status Legend</h4>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded bg-yellow-500"></div>
                                        <span>Pending Design</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded bg-blue-500"></div>
                                        <span>In Design</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded bg-green-500"></div>
                                        <span>Design Complete</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded bg-orange-500"></div>
                                        <span>Pending Estimation</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded bg-purple-500"></div>
                                        <span>In Estimation</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded bg-emerald-500"></div>
                                        <span>Estimation Complete</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded bg-teal-500"></div>
                                        <span>Ready for Delivery</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded bg-gray-500"></div>
                                        <span>Completed</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </>
                {showMoveModal && <MobileScheduleMoveDialog companyState={company?.state} loadCounts={month => loadWeeklyCalendarCounts(month, weekStart => getScheduleAssignments(company.id, quoteType, { weekStart, showOnlyMine: showOnlyMine ? userProfile.id : null, userId: selectedUser || null, designStatusFilter: designStatusFilter !== 'all' ? designStatusFilter : null, cancelledFilter }))}
                    title="Move assignment"
                    allowUnscheduled={!!(selectedAssignment?.quote_info?.expected_delivery_date || selectedAssignment?.quote_info?.expected_finish_date)}
                    jobLabel={selectedAssignment?.schedule_row_id || selectedAssignment?.unique_id}
                    onClose={() => setShowMoveModal(false)} onSelect={handleMobileMoveConfirm}
                />}

                {/* Confirmation Popup */}
                <ConfirmationPopup
                    isOpen={showConfirmation}
                    title={pastDateWarning ? "⚠️ Moving to Past Date" : "Confirm Schedule Change"}
                    message={
                        pendingMove ? (
                            <div className="space-y-4 text-sm text-gray-700">
                                <p>
                                    {pastDateWarning
                                        ? `Warning: You are scheduling "${pendingMove.assignment.unique_id}" to ${pendingMove.newDate.toLocaleDateString('en-AU')}, which is in the past.`
                                        : `Are you sure you want to reschedule "${pendingMove.assignment.unique_id}" to ${pendingMove?.newDate ? pendingMove.newDate.toLocaleDateString('en-AU') : 'make Unscheduled'}?`}
                                </p>

                                {orderWarnings.length > 0 && (
                                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900 max-h-[250px] overflow-y-auto">
                                        <div className="flex items-start gap-2">
                                            <FiAlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                                            <div className="min-w-0 flex-1 items-start text-start">
                                                <p className="font-semibold">
                                                    This may break the delivery order
                                                </p>
                                                <p className="mt-1 text-xs leading-5 text-amber-800">
                                                    Jobs are expected to be sent in this order: Wall, Floor, then Roof.
                                                    Some related departments have dates that may conflict with this move.
                                                </p>

                                                <div className="mt-3 space-y-2">
                                                    {visibleOrderWarnings.map(item => (
                                                        <div
                                                            key={item.id}
                                                            className="rounded-lg border border-amber-200 bg-white/70 px-3 py-2 text-xs"
                                                        >
                                                            <div className="flex items-center justify-between gap-2">
                                                                <span className="font-bold text-amber-950">
                                                                    {item.child_unique_id}
                                                                </span>
                                                                <span className="rounded-md bg-amber-100 px-2 py-0.5 font-semibold capitalize text-amber-800">
                                                                    {item.quote_type}
                                                                </span>
                                                            </div>
                                                            <p className="mt-1 text-amber-800">
                                                                {item.message}
                                                            </p>
                                                            <p className="mt-1 font-semibold text-amber-900">
                                                                Current date: {formatWarningDate(item.date)}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>

                                                {hiddenOrderWarningCount > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowOrderWarningDetails(prev => !prev);
                                                        }}
                                                        className="mt-3 text-xs font-bold text-amber-900 underline"
                                                    >
                                                        {showOrderWarningDetails
                                                            ? 'Show fewer warnings'
                                                            : `Show ${hiddenOrderWarningCount} more`}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <p className="font-medium">
                                    Are you sure you want to proceed?
                                </p>
                            </div>
                        ) : ""
                    }
                    confirmText={pastDateWarning ? "Yes, Schedule to Past Date" : "Reschedule Assignment"}
                    cancelText="Cancel"
                    onConfirm={handleConfirmMove}
                    onCancel={handleCancelMove}
                />

                {/* Date Picker Modal */}
                {showDatePicker && (
                    <div className="mobile-dialog-shell fixed inset-0 !mt-0 bg-white bg-opacity-50 flex items-center justify-center z-[200] p-4">
                        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
                            {/* Header */}
                            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">Select Week</h3>
                                    <p className="text-sm text-gray-600 mt-1">Choose any date to jump to that week</p>
                                </div>
                                <button
                                    onClick={() => setShowDatePicker(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
                                >
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Date Picker */}
                            <div className="p-6 flex justify-center">
                                <DatePicker
                                    selected={datePickerValue}
                                    onChange={handleDatePickerSelect}
                                    inline
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    calendarStartDay={1}
                                    dateFormat="dd/MM/yyyy"
                                    dayClassName={getDayClassName}
                                    renderDayContents={renderDayContents}
                                    filterDate={isDateDisabled}
                                    className="date-picker-custom"
                                />
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
                                <button
                                    onClick={() => setShowDatePicker(false)}
                                    className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                {weekOffset !== 0 && (
                                    <button
                                        onClick={() => {
                                            setWeekOffset(0);
                                            setShowDatePicker(false);
                                        }}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
                                    >
                                        <FiCalendar className="w-4 h-4" />
                                        Jump to This Week
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Drag Date Picker Modal */}
                {showDragDatePicker && draggedForDatePicker && (
                    <div
                        className="mobile-dialog-shell fixed inset-0 !mt-0 flex items-center justify-center z-[200] p-4"
                        style={{ pointerEvents: 'auto' }}
                    >
                        {/* Backdrop */}
                        <div
                            className="absolute inset-0 bg-black bg-opacity-50"
                            onClick={() => {
                                setShowDragDatePicker(false);
                                setDraggedForDatePicker(null);
                                setSelectedDateFromPicker(null);
                                setActiveId(null);
                            }}
                        />

                        {/* Modal Content */}
                        <div className="bg-white rounded-xl shadow-2xl overflow-hidden max-w-md w-full relative z-10">
                            {/* Header */}
                            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-purple-100">
                                <div className="flex-1">
                                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                        <TbTruckDelivery className="w-5 h-5 text-purple-600" />
                                        Schedule Assignment
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Dragging: <span className="font-semibold">{draggedForDatePicker.unique_id}</span>
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Click a date, then confirm to schedule
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowDragDatePicker(false);
                                        setDraggedForDatePicker(null);
                                        setSelectedDateFromPicker(null);
                                        setActiveId(null);
                                    }}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
                                >
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Date Picker */}
                            <div className="p-6 flex justify-center bg-gray-50">
                                <DatePicker
                                    onChange={handleDragDatePickerSelect}
                                    inline
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    calendarStartDay={1}
                                    dateFormat="dd/MM/yyyy"
                                    dayClassName={getDragDatePickerDayClass}
                                    renderDayContents={renderDayContents}
                                    filterDate={isDateDisabled}
                                    className="date-picker-custom"
                                />
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-white gap-3">
                                <button
                                    onClick={() => {
                                        setShowDragDatePicker(false);
                                        setDraggedForDatePicker(null);
                                        setSelectedDateFromPicker(null);
                                        setActiveId(null);
                                    }}
                                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDropOnDatePickerDay}
                                    disabled={!selectedDateFromPicker}
                                    className={`flex-1 px-4 py-2 rounded-lg transition-all font-medium flex items-center justify-center gap-2 ${selectedDateFromPicker
                                        ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 shadow-lg'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        }`}
                                >
                                    <FiCalendar className="w-4 h-4" />
                                    {selectedDateFromPicker
                                        ? `Schedule for ${selectedDateFromPicker.toLocaleDateString('en-AU')}`
                                        : 'Select a Date'
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Drag Overlay */}
                <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={dropAnimationConfig}>
                    {draggedAssignment ? (
                        <div
                            className="bg-white border-2 border-blue-500 rounded-lg p-3 shadow-2xl pointer-events-none"
                            style={{ opacity: 0.9 }}
                        >
                            <div className={`w-full h-1 rounded-full mb-3 ${getMainStatusColor(draggedAssignment.main_status)}`}></div>

                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <FiHash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                    <span className="font-semibold text-gray-900 text-sm truncate">
                                        {draggedAssignment?.quote_info?.child_unique_id || draggedAssignment.unique_id}
                                    </span>
                                </div>
                                {(draggedAssignment.urgent_design) && (
                                    <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded-full">
                                        URGENT
                                    </span>
                                )}
                            </div>

                            <div className="flex items-center gap-2 mb-3">
                                <FiUser className="w-3 h-3 text-blue-500 flex-shrink-0" />
                                <span className="text-sm text-gray-700 truncate font-medium">
                                    {draggedAssignment.customer_name}
                                </span>
                            </div>

                            <div className="mb-3">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${draggedAssignment.job_type === 'design' ? 'bg-blue-100 text-blue-800' :
                                    draggedAssignment.job_type === 'estimation' ? 'bg-purple-100 text-purple-800' :
                                        'bg-green-100 text-green-800'
                                    }`}>
                                    {draggedAssignment.job_type === 'design' && <FiTool className="w-3 h-3 mr-1" />}
                                    {draggedAssignment.job_type === 'estimation' && <FiDollarSign className="w-3 h-3 mr-1" />}
                                    {draggedAssignment.job_type === 'design_estimation' && <FiTool className="w-3 h-3 mr-1" />}
                                    {draggedAssignment.job_type?.replace('_', ' & ')}
                                </span>
                            </div>
                        </div>
                    ) : null}
                </DragOverlay>


            </DndContext>

            {editOpen && editJobId && (
                <EditAssignment
                    modalJobId={editJobId}
                    modalCompanySlug={companySlug}
                    onClose={() => {
                        setEditOpen(false);
                        setEditJobId(null);
                    }}
                />
            )}
        </div>
    );
};

export default QuoteSchedule;
